import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  initAnalytics,
  trackPageView,
  trackViewItem,
  trackAddToCart,
  trackBeginCheckout,
  trackSearch,
  trackSignUp,
  trackLead,
} from "./analytics";
import {
  AboutPage,
  FAQPage,
  PrivacyPage,
  TermsPage,
  ReturnsPage,
  ShippingPage,
  CookiesPage,
  WholesaleInfoPage,
} from "./InfoPages";
import {
  CareersPage, OpportunityDetailPage, OpportunityApplyPage, ApplicationSuccessPage,
} from "./Careers";
import CookieConsent from "./CookieConsent";
import { BrandLogo } from "./BrandLogos";
import { isBundleProduct, BundleCard, BundleDealsSection, BundleDetailLayout } from "./Bundle";
import InsideCrown01 from "./InsideCrown01";
import FirstTimeOfferBar from "./MysteryOffer";
import DispatchNote from "./DispatchNote";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* -------------------- Cart Context -------------------- */
const CartCtx = createContext(null);
const useCart = () => useContext(CartCtx);

function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("crown01_cart") || "[]"); } catch { return []; }
  });
  const [open, setOpen] = useState(false);
  const [knownEmail, setKnownEmail] = useState(() => localStorage.getItem("crown01_known_email") || "");

  useEffect(() => {
    localStorage.setItem("crown01_cart", JSON.stringify(items));
  }, [items]);

  // Identify the cart on the backend so abandoned-cart reminders can be sent.
  // Called automatically whenever a user provides their email via any form.
  const identifyCart = async (email, { silent = false } = {}) => {
    if (!email || items.length === 0) return false;
    try {
      const payload = {
        email,
        items: items.map(i => ({
          variant_id: i.variant_id,
          product_id: i.product_id || "",
          slug: i.slug || "",
          name: i.name,
          image: i.image,
          variant_title: i.variant_title || "",
          unit_price: i.unit_price,
          qty: i.qty,
        })),
      };
      await axios.post(`${API}/cart/identify`, payload);
      localStorage.setItem("crown01_known_email", email);
      setKnownEmail(email);
      return true;
    } catch (e) {
      return false;
    }
  };

  const completeCart = async () => {
    const email = knownEmail || localStorage.getItem("crown01_known_email");
    if (!email) return;
    try { await axios.post(`${API}/cart/complete`, { email }); } catch (e) { /* ignore */ }
  };

  /**
   * Add a Shopify product to cart.
   * variant = ProductVariant object from Shopify (has id, title, price)
   */
  const add = (product, variant, qty = 1) => {
    if (!variant || !variant.id) {
      toast.error("Out of stock");
      return;
    }
    setItems(prev => {
      const key = variant.id;
      const found = prev.find(i => i.key === key);
      if (found) {
        return prev.map(i => i.key === key ? { ...i, qty: i.qty + qty } : i);
      }
      return [...prev, {
        key,
        variant_id: variant.id,
        product_id: product.id,
        slug: product.slug,
        name: product.name,
        image: product.image,
        variant_title: variant.title,
        unit_price: variant.price,
        qty,
      }];
    });
    const variantSuffix = variant.title && variant.title.toLowerCase() !== "default title" ? ` — ${variant.title}` : "";
    toast.success("Added to cart", { description: `${product.name}${variantSuffix}` });
    setOpen(true);
    // Analytics — GA4 / Meta / TikTok AddToCart
    try { trackAddToCart(product, variant, qty); } catch (e) { /* noop */ }
  };

  const checkout = async () => {
    if (items.length === 0) return;
    try {
      const email = knownEmail || localStorage.getItem("crown01_known_email") || undefined;
      // Analytics — fire begin_checkout / InitiateCheckout BEFORE the redirect
      try { trackBeginCheckout(items, subtotal); } catch (e) { /* noop */ }
      const r = await axios.post(`${API}/cart/checkout`, {
        items: items.map(i => ({ variant_id: i.variant_id, qty: i.qty, name: i.name, unit_price: i.unit_price })),
        email,
      });
      if (r.data?.checkout_url) {
        completeCart();
        window.location.href = r.data.checkout_url;
      } else {
        toast.error("Could not start checkout. Please try again.");
      }
    } catch (e) {
      toast.error("Checkout error", { description: e.response?.data?.detail || "Please try again." });
    }
  };

  const remove = (key) => setItems(prev => prev.filter(i => i.key !== key));
  const update = (key, qty) => setItems(prev => prev.map(i => i.key === key ? { ...i, qty: Math.max(1, qty) } : i));
  const clear = () => setItems([]);

  const subtotal = items.reduce((s, i) => s + i.unit_price * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);

  return (
    <CartCtx.Provider value={{ items, add, remove, update, clear, subtotal, count, open, setOpen, identifyCart, completeCart, checkout, knownEmail }}>
      {children}
    </CartCtx.Provider>
  );
}

/* -------------------- Shared UI -------------------- */
const Stars = ({ value = 5, size = "text-xs" }) => (
  <span className={`inline-flex items-center gap-[1px] ${size}`} style={{ color: "var(--c-gold)" }} data-testid="stars">
    {[1,2,3,4,5].map(n => (
      <i key={n} className={`fa-solid fa-star ${n <= Math.round(value) ? "" : "opacity-25"}`}></i>
    ))}
  </span>
);

const SectionTitle = ({ eyebrow, title, subtitle, align = "left" }) => (
  <div className={`max-w-2xl ${align === "center" ? "mx-auto text-center" : ""}`}>
    {eyebrow && (
      <div className={`flex items-center gap-3 mb-3 ${align === "center" ? "justify-center" : ""}`}>
        <span className="gold-line" />
        <span className="text-xs font-semibold tracking-[0.18em] uppercase" style={{ color: "var(--c-gold-deep)" }}>{eyebrow}</span>
        <span className="gold-line" style={{ transform: "scaleX(-1)" }} />
      </div>
    )}
    <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-black leading-[1.05]">{title}</h2>
    {subtitle && <p className="mt-4 text-base md:text-lg text-[color:var(--c-grey-500)]">{subtitle}</p>}
  </div>
);

/* -------------------- Header -------------------- */

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2.5 group shrink-0" data-testid="logo">
      <img
        src="/brand/crown01-logo.png"
        srcSet="/brand/crown01-logo.png 1x, /brand/crown01-logo@2x.png 2x"
        alt="Crown01 — Premium UK Supplier"
        width="44"
        height="44"
        className="h-11 w-11 md:h-12 md:w-12 rounded-lg select-none"
        draggable="false"
        data-testid="logo-img"
      />
      <div className="leading-none">
        <div className="font-display text-lg sm:text-xl tracking-tight text-black">Crown01</div>
        <div className="text-[10px] tracking-[0.22em] uppercase" style={{ color: "var(--c-gold-deep)" }}>UK Supplier</div>
      </div>
    </Link>
  );
}

function Header() {
  const { count, setOpen } = useCart();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [q, setQ] = useState("");

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      try { trackSearch(q.trim()); } catch (e) { /* noop */ }
      navigate(`/shop?search=${encodeURIComponent(q.trim())}`);
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[color:var(--c-grey-100)] overflow-x-clip" data-testid="site-header">
      <FirstTimeOfferBar />
      <div className="max-w-7xl mx-auto px-4 sm:px-5 md:px-8 py-4 flex items-center gap-2 sm:gap-3 md:gap-6">
        <Logo />
        <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-xl relative" data-testid="search-form">
          <input
            value={q} onChange={e => setQ(e.target.value)}
            className="input-bare w-full pr-12"
            placeholder="Search bin bags, gloves, kitchen rolls..."
            data-testid="search-input"
          />
          <button type="submit" className="absolute right-1.5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center text-white"
            style={{ background: "var(--c-black)" }} aria-label="Search" data-testid="search-btn">
            <i className="fa-solid fa-magnifying-glass text-sm"></i>
          </button>
        </form>
        <nav className="hidden lg:flex items-center gap-5 text-sm">
          <Link to="/shop" className="nav-link" data-testid="nav-shop">Shop All</Link>
          <Link to="/shop?tag=bundle" className="nav-link font-semibold" style={{ color: "var(--c-gold-deep)" }} data-testid="nav-bundles"><i className="fa-solid fa-gem text-[10px] mr-1"></i>Bundles</Link>
          <Link to="/shop?category=bin-bags-and-refuse-sacks" className="nav-link" data-testid="nav-bin-bags">Bin Bags</Link>
          <Link to="/shop?category=carrier-bags-shopping-bags" className="nav-link" data-testid="nav-carrier-bags">Carrier Bags</Link>
          <Link to="/shop?category=soft-serviettes-napkins" className="nav-link" data-testid="nav-serviettes">Serviettes</Link>
          <Link to="/shop?category=view-our-catering-products" className="nav-link" data-testid="nav-catering">Catering</Link>
          <Link to="/about" className="nav-link" data-testid="nav-about">About</Link>
          <Link to="/careers" className="nav-link" data-testid="nav-careers">Careers</Link>
          <Link to="/faq" className="nav-link" data-testid="nav-faq">FAQ</Link>
          <Link to="/contact" className="nav-link" data-testid="nav-contact">Contact</Link>
        </nav>
        <div className="flex items-center gap-1.5 md:gap-2 ml-auto shrink-0">
          <Link to="/trade" className="hidden md:inline-flex btn !py-2 !px-4 !text-xs font-semibold" style={{ background: "var(--c-gold)", color: "var(--c-black)" }} data-testid="header-trade-cta">
            <i className="fa-solid fa-handshake text-[12px]"></i> Trade &amp; Wholesale
          </Link>
          <button onClick={() => setOpen(true)} className="relative w-11 h-11 min-w-[44px] rounded-full bg-[color:var(--c-grey-50)] hover:bg-[color:var(--c-grey-100)] flex items-center justify-center transition shrink-0" aria-label="Cart" data-testid="cart-icon-btn">
            <i className="fa-solid fa-bag-shopping"></i>
            {count > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full text-[11px] font-bold text-white flex items-center justify-center"
                style={{ background: "var(--c-red)" }} data-testid="cart-count">{count}</span>
            )}
          </button>
          <button className="lg:hidden w-11 h-11 min-w-[44px] rounded-full bg-[color:var(--c-grey-50)] flex items-center justify-center shrink-0" onClick={() => setMobileOpen(v => !v)} aria-label="Menu" data-testid="mobile-menu-btn">
            <i className={`fa-solid ${mobileOpen ? "fa-xmark" : "fa-bars"}`}></i>
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="lg:hidden border-t border-[color:var(--c-grey-100)] bg-white" data-testid="mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-3">
            <form onSubmit={onSearch} className="relative">
              <input value={q} onChange={e => setQ(e.target.value)} className="input-bare w-full pr-12" placeholder="Search..." />
              <button className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full text-white flex items-center justify-center" style={{ background: "var(--c-black)" }}><i className="fa-solid fa-magnifying-glass text-sm"></i></button>
            </form>
            <Link to="/shop" className="py-2 border-b border-[color:var(--c-grey-100)]" onClick={() => setMobileOpen(false)}>Shop All</Link>
            <Link to="/shop?category=bin-bags-and-refuse-sacks" className="py-2 border-b border-[color:var(--c-grey-100)]" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-bin-bags">Bin Bags</Link>
            <Link to="/shop?category=carrier-bags-shopping-bags" className="py-2 border-b border-[color:var(--c-grey-100)]" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-carrier-bags">Carrier Bags</Link>
            <Link to="/shop?category=soft-serviettes-napkins" className="py-2 border-b border-[color:var(--c-grey-100)]" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-serviettes">Serviettes</Link>
            <Link to="/shop?category=view-our-catering-products" className="py-2 border-b border-[color:var(--c-grey-100)]" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-catering">Catering</Link>
            <Link to="/trade" className="py-2 border-b border-[color:var(--c-grey-100)] font-semibold" style={{ color: "var(--c-gold-deep)" }} onClick={() => setMobileOpen(false)} data-testid="mobile-nav-trade"><i className="fa-solid fa-handshake mr-2"></i>Trade & Wholesale</Link>
            <Link to="/shop?tag=bundle" className="py-2 border-b border-[color:var(--c-grey-100)] font-semibold" style={{ color: "var(--c-gold-deep)" }} onClick={() => setMobileOpen(false)} data-testid="mobile-nav-bundles"><i className="fa-solid fa-gem mr-2"></i>Bundles</Link>
            <Link to="/about" className="py-2 border-b border-[color:var(--c-grey-100)]" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-about">About</Link>
            <Link to="/careers" className="py-2 border-b border-[color:var(--c-grey-100)]" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-careers">Careers</Link>
            <Link to="/faq" className="py-2 border-b border-[color:var(--c-grey-100)]" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-faq">FAQ</Link>
            <Link to="/contact" className="py-2" onClick={() => setMobileOpen(false)}>Contact</Link>
          </div>
        </div>
      )}
    </header>
  );
}

/* -------------------- Cart Drawer -------------------- */
function CartDrawer() {
  const { items, open, setOpen, remove, update, subtotal, clear, identifyCart, checkout, knownEmail } = useCart();
  const [saveEmail, setSaveEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  const onSaveCart = async (e) => {
    e.preventDefault();
    if (!saveEmail) return;
    setSaving(true);
    await identifyCart(saveEmail);
    setSaving(false);
  };

  const onCheckout = async () => {
    setCheckingOut(true);
    await checkout();
    setCheckingOut(false);
  };

  return (
    <>
      <div className={`fixed inset-0 bg-black/40 z-50 transition-opacity ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`} onClick={() => setOpen(false)} />
      <aside className={`fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-2xl transition-transform ${open ? "translate-x-0" : "translate-x-full"}`} data-testid="cart-drawer">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[color:var(--c-grey-100)]">
          <div>
            <div className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--c-gold-deep)" }}>Your Cart</div>
            <div className="font-display text-2xl">{items.length} item{items.length !== 1 ? "s" : ""}</div>
          </div>
          <button onClick={() => setOpen(false)} className="w-10 h-10 rounded-full hover:bg-[color:var(--c-grey-50)] flex items-center justify-center" aria-label="Close" data-testid="cart-close">
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div className="overflow-y-auto px-6 py-4" style={{ height: items.length === 0 ? "calc(100% - 88px)" : "calc(100% - 332px)" }}>
          {items.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-4" style={{ background: "var(--c-grey-50)" }}>
                <i className="fa-solid fa-bag-shopping text-2xl" style={{ color: "var(--c-grey-500)" }}></i>
              </div>
              <p className="text-[color:var(--c-grey-500)] mb-6">Your cart is empty</p>
              <Link to="/shop" onClick={() => setOpen(false)} className="btn btn-black" data-testid="cart-shop-btn">Browse Products</Link>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map(i => (
                <div key={i.key} className="flex gap-4 pb-4 border-b border-[color:var(--c-grey-100)]" data-testid="cart-item">
                  <img src={i.image} alt={i.name} className="w-20 h-20 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm leading-tight line-clamp-2">{i.name}</div>
                    {i.variant_title && i.variant_title.toLowerCase() !== "default title" && (
                      <div className="text-xs text-[color:var(--c-grey-500)] mt-1">{i.variant_title}</div>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <div className="inline-flex items-center border border-[color:var(--c-grey-200)] rounded-full overflow-hidden">
                        <button onClick={() => update(i.key, i.qty - 1)} className="w-7 h-7 hover:bg-[color:var(--c-grey-50)]" data-testid={`cart-qty-dec-${i.key}`}>−</button>
                        <span className="px-2 text-sm font-semibold" data-testid={`cart-qty-value-${i.key}`}>{i.qty}</span>
                        <button onClick={() => update(i.key, i.qty + 1)} className="w-7 h-7 hover:bg-[color:var(--c-grey-50)]" data-testid={`cart-qty-inc-${i.key}`}>+</button>
                      </div>
                      <div className="font-semibold" data-testid={`cart-line-subtotal-${i.key}`}>£{(i.unit_price * i.qty).toFixed(2)}</div>
                    </div>
                  </div>
                  <button onClick={() => remove(i.key)} className="text-[color:var(--c-grey-500)] hover:text-[color:var(--c-red)] self-start" aria-label="Remove">
                    <i className="fa-solid fa-trash text-sm"></i>
                  </button>
                </div>
              ))}
              <button onClick={clear} className="text-xs text-[color:var(--c-grey-500)] underline">Clear cart</button>
            </div>
          )}
        </div>
        {items.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 border-t border-[color:var(--c-grey-100)] bg-white px-6 py-4">
            {/* Save cart for later (abandoned-cart capture) */}
            {knownEmail ? (
              <div className="mb-3 p-2.5 rounded-lg text-xs flex items-center gap-2" style={{ background: "var(--c-cream)", border: "1px solid var(--c-grey-100)", color: "var(--c-gold-deep)" }} data-testid="cart-saved-state">
                <i className="fa-solid fa-circle-check"></i>
                <span className="font-semibold text-[color:var(--c-black)]">Cart saved</span>
                <span className="text-[color:var(--c-grey-500)] truncate">— {knownEmail}</span>
              </div>
            ) : (
              <form onSubmit={onSaveCart} className="mb-3 flex gap-2" data-testid="cart-save-form">
                <input type="email" required placeholder="Save cart — email for reminders" value={saveEmail} onChange={e => setSaveEmail(e.target.value)} className="input-bare flex-1 !py-2 !text-xs" data-testid="cart-save-email" />
                <button disabled={saving} className="btn btn-outline-black !py-2 !px-3 !text-xs" data-testid="cart-save-btn">
                  {saving ? "…" : "Save"}
                </button>
              </form>
            )}
            <div className="flex justify-between mb-1">
              <span className="text-[color:var(--c-grey-500)]">Subtotal</span>
              <span className="font-display text-xl">£{subtotal.toFixed(2)}</span>
            </div>
            <p className="text-xs text-[color:var(--c-grey-500)] mb-3">Shipping & taxes calculated at checkout. Fast UK delivery.</p>

            {/* Secure-checkout trust badge — subtle, on-brand, sits directly above CTA */}
            <div
              className="flex items-start gap-2.5 rounded-lg px-3 py-2.5 mb-3"
              style={{
                background: "rgba(212,175,55,0.06)",
                border: "1px solid rgba(212,175,55,0.28)",
              }}
              data-testid="secure-checkout-badge"
              role="note"
            >
              <i
                className="fa-solid fa-lock mt-0.5 text-[13px] shrink-0"
                style={{ color: "var(--c-gold-deep)" }}
                aria-hidden="true"
              ></i>
              <div className="leading-snug">
                <div className="text-[12px] font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--c-black)" }}>
                  Secure Checkout
                </div>
                <p className="text-[11px] mt-0.5" style={{ color: "var(--c-grey-500)" }}>
                  Payments securely processed by Shopify, a PCI-DSS Level&nbsp;1 compliant platform. Crown01 never stores payment card details.
                </p>
              </div>
            </div>

            <DispatchNote />

            <button className="btn btn-red w-full disabled:opacity-60" data-testid="cart-checkout-btn" onClick={onCheckout} disabled={checkingOut}>
              {checkingOut ? "Redirecting to checkout…" : (<>Checkout — £{subtotal.toFixed(2)} <i className="fa-solid fa-arrow-right"></i></>)}
            </button>
            <Link to="/shop" onClick={() => setOpen(false)} className="block text-center mt-2 text-sm underline">Continue shopping</Link>
          </div>
        )}
      </aside>
    </>
  );
}

/* -------------------- SEO helper -------------------- */
const SITE_URL = "https://crown01.co.uk";
const DEFAULT_OG = "https://e7qcac-xy.myshopify.com/cdn/shop/files/IMG_6978_2.jpg?v=1771961263&width=1200";

function SEO({ title, description, path = "/", image, jsonLd }) {
  const fullTitle = title ? `${title} | Crown01` : "Crown01 — UK Supplier of Packaging, Cleaning & Trade Products";
  const desc = description || "Trusted UK supplier of packaging, cleaning & trade products. Single packs, full boxes, wholesale and pallet orders. Fast UK delivery.";
  const url = `${SITE_URL}${path}`;
  const img = image || DEFAULT_OG;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
}

/* -------------------- Brand constants -------------------- */
// Official Crown01 channels — provided by store owner
const SOCIAL_LINKS = {
  website: { name: "Crown01.co.uk", url: "https://crown01.co.uk", icon: "fa-solid fa-globe", color: "var(--c-black)" },
  amazon: { name: "Amazon", url: "https://www.amazon.co.uk/stores/Crown01/page/E7BB388C-43FA-4C85-B94C-5B85E1562C08?lp_asin=B0F5QN94ZT&ref_=ast_bln", icon: "fa-brands fa-amazon", color: "#FF9900" },
  tiktok: { name: "TikTok Shop", url: "https://vt.tiktok.com/ZNRcjhUKy/?page=TikTokShop&utm_campaign=client_share&utm_source=more&share_app_id=1233", icon: "fa-brands fa-tiktok", color: "#000000" },
  ebay: { name: "eBay", url: "https://www.ebay.co.uk/usr/nar-london", icon: "fa-brands fa-ebay", color: "#E53238" },
  instagram: { name: "Instagram", url: "https://www.instagram.com/crown01_uk/", icon: "fa-brands fa-instagram", color: "#E1306C" },
  facebook: { name: "Facebook", url: "https://facebook.com/Crown01Uk", icon: "fa-brands fa-facebook-f", color: "#1877F2" },
  linkedin: { name: "LinkedIn", url: "https://www.linkedin.com/in/nar-london-2b061b414", icon: "fa-brands fa-linkedin-in", color: "#0A66C2" },
};

/* -------------------- Trust & Community -------------------- */
function TrustCommunity() {
  const marketplaces = [
    { ...SOCIAL_LINKS.website, label: "Our Website" },
    { ...SOCIAL_LINKS.amazon, label: "Amazon Store" },
    { ...SOCIAL_LINKS.tiktok, label: "TikTok Shop" },
    { ...SOCIAL_LINKS.ebay, label: "eBay Store" },
  ];
  const socials = [
    { ...SOCIAL_LINKS.instagram, handle: "@crown01_uk" },
    { ...SOCIAL_LINKS.facebook, handle: "Crown01 UK" },
    { ...SOCIAL_LINKS.linkedin, handle: "Crown01" },
  ];

  // Use real product imagery as IG placeholder tiles (until live feed wired)
  const igTiles = [
    "https://e7qcac-xy.myshopify.com/cdn/shop/files/37.jpg?v=1750273596&width=400",
    "https://e7qcac-xy.myshopify.com/cdn/shop/files/IMG_6978_2.jpg?v=1771961263&width=400",
    "https://e7qcac-xy.myshopify.com/cdn/shop/files/26.jpg?v=1765068761&width=400",
    "https://e7qcac-xy.myshopify.com/cdn/shop/files/20.jpg?v=1750946093&width=400",
    "https://e7qcac-xy.myshopify.com/cdn/shop/files/1.png?v=1749816336&width=400",
    "https://e7qcac-xy.myshopify.com/cdn/shop/files/27.jpg?v=1765068925&width=400",
  ];

  return (
    <section className="py-16 md:py-24 bg-white" data-testid="trust-community">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <SectionTitle eyebrow="Trust & Community" title="Trusted across multiple platforms" subtitle="Customers shop with Crown01 through our website, marketplaces and social channels — whichever is most convenient." align="center" />

        {/* NAR London trust strip */}
        <div className="mt-8 max-w-4xl mx-auto rounded-2xl p-5 md:p-6 text-center" style={{ background: "var(--c-grey-50)", border: "1px solid var(--c-grey-100)" }} data-testid="nar-trust-strip">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-1.5" style={{ color: "var(--c-gold-deep)" }}>Company Trust</div>
          <p className="text-sm md:text-base text-[color:var(--c-grey-700)] leading-relaxed">
            Operated by <strong className="text-black">NAR London Ltd</strong>, a UK-based supplier of packaging, cleaning, catering and wholesale consumable products. We supply retail customers, businesses, wholesalers and pallet buyers across the United Kingdom.
          </p>
          <div className="mt-2 text-xs text-[color:var(--c-grey-500)]">
            <i className="fa-solid fa-location-dot mr-1.5" style={{ color: "var(--c-gold-deep)" }}></i>
            95 Theobalds Park Road, Enfield, EN2 9BS · <a href="mailto:info@crown01.co.uk" className="underline">info@crown01.co.uk</a>
          </div>
        </div>

        {/* Sales channels */}
        <div className="mt-12">
          <div className="text-center text-xs uppercase tracking-[0.18em] font-semibold mb-5" style={{ color: "var(--c-gold-deep)" }}>Shop With Us</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-testid="marketplace-grid">
            {marketplaces.map(m => (
              <a key={m.name} href={m.url} target={m.url === "#" ? "_self" : "_blank"} rel="noopener noreferrer" className="group flex flex-col items-center justify-center p-5 md:p-6 rounded-2xl lift" style={{ background: "var(--c-grey-50)", border: "1px solid var(--c-grey-100)" }} data-testid={`marketplace-${m.name.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center mb-3 transition" style={{ background: "#fff", border: "1px solid var(--c-grey-100)" }}>
                  <BrandLogo name={m.name} size={32} />
                </div>
                <div className="text-[10px] uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--c-gold-deep)" }}>Available on</div>
                <div className="font-display text-base md:text-lg text-black mt-0.5">{m.name}</div>
                <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-[color:var(--c-grey-500)] group-hover:text-[color:var(--c-black)] transition">
                  Visit <i className="fa-solid fa-arrow-up-right-from-square text-[10px]"></i>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="my-12 rule-gold"></div>

        {/* Social channels */}
        <div>
          <div className="text-center text-xs uppercase tracking-[0.18em] font-semibold mb-2" style={{ color: "var(--c-gold-deep)" }}>Join Our Community</div>
          <div className="text-center font-display text-2xl md:text-3xl text-black">Follow Crown01</div>
          <p className="text-center mt-2 text-sm text-[color:var(--c-grey-700)]">Follow us for offers and new products.</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3" data-testid="social-grid">
            {socials.map(s => (
              <a key={s.name} href={s.url} target={s.url === "#" ? "_self" : "_blank"} rel="noopener noreferrer" className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full transition" style={{ background: "var(--c-black)", color: "#fff" }} data-testid={`social-${s.name.toLowerCase()}`}>
                <i className={`${s.icon} text-base`} style={{ color: s.color }}></i>
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-[0.16em] font-semibold leading-none" style={{ color: "var(--c-gold)" }}>{s.name}</div>
                  <div className="text-xs font-semibold leading-tight">{s.handle}</div>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Instagram feed placeholder */}
        <div className="mt-14">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-5">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--c-gold-deep)" }}>@crown01_uk on Instagram</div>
              <div className="font-display text-2xl text-black mt-1">Latest from our community</div>
            </div>
            <a href={SOCIAL_LINKS.instagram.url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-black !py-2 !text-xs" data-testid="instagram-follow-btn">
              <i className="fa-brands fa-instagram"></i> Follow @crown01_uk
            </a>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-3" data-testid="instagram-grid">
            {igTiles.map((src, i) => (
              <a key={i} href={SOCIAL_LINKS.instagram.url} target="_blank" rel="noopener noreferrer" className="group relative aspect-square rounded-xl overflow-hidden bg-[color:var(--c-grey-50)]" data-testid={`ig-tile-${i}`}>
                <img src={src} alt="Crown01 on Instagram" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center">
                  <i className="fa-brands fa-instagram text-white text-xl md:text-2xl opacity-0 group-hover:opacity-100 transition"></i>
                </div>
              </a>
            ))}
          </div>
        </div>

        <p className="mt-12 text-center text-sm text-[color:var(--c-grey-700)] max-w-2xl mx-auto">
          <i className="fa-solid fa-circle-check mr-2" style={{ color: "var(--c-gold-deep)" }}></i>
          Join our growing community and shop through your preferred platform — single packs, full boxes, wholesale or pallet, the choice is yours.
        </p>
      </div>
    </section>
  );
}

/* -------------------- Home -------------------- */
function Hero() {
  return (
    <section className="relative bg-white hero-pattern overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-6" style={{ background: "var(--c-grey-50)", color: "var(--c-gold-deep)", border: "1px solid var(--c-grey-100)" }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--c-red)" }}></span>
            Trusted UK Supplier · Retail · Trade · Wholesale
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-black">
            Whether you need <span className="text-gradient-gold">1 pack</span> or <span className="text-gradient-gold">1 pallet</span>, we've got you covered.
          </h1>
          <p className="mt-5 text-base md:text-lg text-[color:var(--c-grey-700)] max-w-xl leading-relaxed">
            Quality packaging, cleaning &amp; household products for homes, businesses &amp; trade customers across the UK. <span className="font-semibold text-black">Buy 1 pack or a full box — the choice is yours.</span>
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3" data-testid="hero-ctas">
            <Link to="/shop" className="btn btn-red" data-testid="hero-shop-single-btn">
              <i className="fa-solid fa-bag-shopping"></i> Shop Single Packs
            </Link>
            <Link to="/shop?bestseller=true" className="btn btn-black" data-testid="hero-shop-wholesale-btn">
              <i className="fa-solid fa-boxes-stacked"></i> Shop Wholesale
            </Link>
            <Link to="/trade" className="btn btn-outline-black" data-testid="hero-pallet-btn">
              Request Pallet Pricing <i className="fa-solid fa-arrow-right"></i>
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm" data-testid="hero-trust-strip">
            <span className="inline-flex items-center gap-2 font-semibold text-black">
              <i className="fa-solid fa-circle-check" style={{ color: "var(--c-gold-deep)" }}></i> 36,000+ units sold on one bestseller
            </span>
            <span className="hidden md:inline opacity-30">·</span>
            <span className="inline-flex items-center gap-2 text-[color:var(--c-grey-700)]">
              4.7★ average across Amazon, eBay &amp; TikTok Shop
            </span>
          </div>
        </div>

        {/* Split image collage: retail packs (left) + warehouse stock (right) */}
        <div className="lg:col-span-6 relative fade-up delay-200">
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            <div className="space-y-3 md:space-y-4">
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-[color:var(--c-grey-50)]">
                <img src="https://e7qcac-xy.myshopify.com/cdn/shop/files/37.jpg?v=1750273596&width=800" alt="Single pack — retail" className="w-full h-full object-cover" />
                <div className="absolute top-3 left-3 badge-pill" style={{ background: "var(--c-red)", color: "#fff" }}>
                  <i className="fa-solid fa-tag text-[9px]"></i> Single Packs
                </div>
                <div className="absolute bottom-3 left-3 right-3 text-white">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--c-gold)" }}>Retail Ready</div>
                  <div className="font-display text-sm md:text-base leading-tight drop-shadow">From £1.50</div>
                </div>
                <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, transparent 55%, rgba(10,10,10,.55))" }} />
              </div>
              <div className="relative aspect-square rounded-2xl overflow-hidden bg-[color:var(--c-grey-50)]">
                <img src="https://e7qcac-xy.myshopify.com/cdn/shop/files/1.png?v=1749816336&width=800" alt="Pack of bin bags" className="w-full h-full object-cover" />
                <div className="absolute top-3 left-3 badge-pill" style={{ background: "var(--c-black)", color: "var(--c-gold)" }}>
                  <i className="fa-solid fa-cube text-[9px]"></i> Full Box Savings
                </div>
              </div>
            </div>
            <div className="space-y-3 md:space-y-4 pt-6 md:pt-10">
              <div className="relative aspect-square rounded-2xl overflow-hidden bg-[color:var(--c-grey-50)]">
                <img src="https://e7qcac-xy.myshopify.com/cdn/shop/files/IMG_6978_2.jpg?v=1771961263&width=800" alt="Crown01 UK warehouse" className="w-full h-full object-cover" />
                <div className="absolute top-3 left-3 badge-pill" style={{ background: "var(--c-gold)", color: "var(--c-black)" }}>
                  <i className="fa-solid fa-warehouse text-[9px]"></i> Real UK Stock
                </div>
              </div>
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-[color:var(--c-grey-50)]">
                <img src="https://e7qcac-xy.myshopify.com/cdn/shop/files/26.jpg?v=1765068761&width=800" alt="Wholesale & pallet quantities" className="w-full h-full object-cover" />
                <div className="absolute top-3 left-3 badge-pill" style={{ background: "var(--c-black)", color: "var(--c-gold)" }}>
                  <i className="fa-solid fa-truck-ramp-box text-[9px]"></i> Wholesale & Pallet
                </div>
                <div className="absolute bottom-3 left-3 right-3 text-white">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--c-gold)" }}>Trade Quantities</div>
                  <div className="font-display text-sm md:text-base leading-tight drop-shadow">Custom quotes in 1hr</div>
                </div>
                <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, transparent 55%, rgba(10,10,10,.55))" }} />
              </div>
            </div>
          </div>
          <div className="hidden md:flex absolute -right-3 -top-3 items-center gap-2 bg-white rounded-full shadow-lg px-4 py-2 z-10" style={{ border: "1px solid var(--c-grey-100)" }}>
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "var(--c-red)" }}></span>
            <span className="text-xs font-semibold">12 orders in the last hour</span>
          </div>
        </div>
      </div>

      {/* Five-icon trust bar */}
      <div className="border-t border-b" style={{ borderColor: "var(--c-grey-100)", background: "var(--c-grey-50)" }} data-testid="hero-trust-bar">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-5 grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { i: "fa-truck-fast", t: "Fast UK Delivery" },
            { i: "fa-box", t: "Single Packs Available" },
            { i: "fa-building", t: "Wholesale Orders Welcome" },
            { i: "fa-truck-ramp-box", t: "Pallet Quantities" },
            { i: "fa-shield-halved", t: "Secure Checkout" },
          ].map(x => (
            <div key={x.t} className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--c-black)" }}>
                <i className={`fa-solid ${x.i} text-sm`} style={{ color: "var(--c-gold)" }}></i>
              </div>
              <div className="text-xs md:text-[13px] font-semibold leading-tight text-black">{x.t}</div>
            </div>
          ))}
        </div>
      </div>
      <Marquee />
    </section>
  );
}

/* -------------------- Shop Your Way -------------------- */
function ShopYourWay() {
  const tiers = [
    {
      key: "retail",
      kicker: "For Homes & Small Businesses",
      title: "Single Packs",
      desc: "Perfect for households, offices, takeaways and shops. Order one pack at a time — no minimums, no fuss.",
      bullets: ["From £1.50 per pack", "Next-day UK delivery", "Mix & match products"],
      cta: "Shop Retail",
      to: "/shop",
      icon: "fa-bag-shopping",
      image: "https://e7qcac-xy.myshopify.com/cdn/shop/files/27.jpg?v=1765068925&width=800",
      tone: "light",
      testid: "tier-retail",
    },
    {
      key: "box",
      kicker: "Most Popular — Save 30%+",
      title: "Full Boxes",
      desc: "Better value for restaurants, cleaners, schools and regular users. Buy in case quantities and stock up.",
      bullets: ["Save 30–50% vs single packs", "Boxed for easy storage", "Bulk-discount tiers"],
      cta: "Shop Box Deals",
      to: "/shop?bestseller=true",
      icon: "fa-cube",
      image: "https://e7qcac-xy.myshopify.com/cdn/shop/files/20.jpg?v=1750946093&width=800",
      tone: "dark",
      testid: "tier-box",
    },
    {
      key: "pallet",
      kicker: "Trade & Wholesale",
      title: "Pallet Orders",
      desc: "For contract cleaners, retailers, distributors and large operations. Custom pricing, Net 30 terms, dedicated account manager.",
      bullets: ["Bespoke pallet pricing", "Net 30 trade terms", "1-hour quote response"],
      cta: "Request Quote",
      to: "/trade",
      icon: "fa-truck-ramp-box",
      image: "https://e7qcac-xy.myshopify.com/cdn/shop/files/IMG_6978_2.jpg?v=1771961263&width=800",
      tone: "light",
      testid: "tier-pallet",
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white" data-testid="shop-your-way">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <SectionTitle eyebrow="Shop Your Way" title="One pack, one box, or one pallet — your choice." subtitle="Crown01 serves every customer the same way: real stock, real pricing, fast UK dispatch. Pick the quantity that suits you." align="center" />
        <div className="mt-12 grid md:grid-cols-3 gap-5 md:gap-6">
          {tiers.map((t, idx) => {
            const isDark = t.tone === "dark";
            return (
              <div key={t.key} data-testid={t.testid} className={`rounded-2xl overflow-hidden lift flex flex-col ${isDark ? "" : "border"}`} style={{ background: isDark ? "var(--c-black)" : "#fff", borderColor: "var(--c-grey-100)", color: isDark ? "#fff" : "inherit" }}>
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={t.image} alt={t.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0" style={{ background: isDark ? "linear-gradient(180deg, rgba(10,10,10,.2), rgba(10,10,10,.55))" : "linear-gradient(180deg, transparent 40%, rgba(10,10,10,.35))" }} />
                  <div className="absolute top-3 left-3 badge-pill" style={{ background: isDark ? "var(--c-gold)" : "var(--c-black)", color: isDark ? "var(--c-black)" : "var(--c-gold)" }}>
                    <i className={`fa-solid ${t.icon} text-[10px]`}></i> Tier {idx + 1}
                  </div>
                </div>
                <div className="p-6 md:p-7 flex flex-col flex-1">
                  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: isDark ? "var(--c-gold)" : "var(--c-gold-deep)" }}>{t.kicker}</div>
                  <h3 className={`font-display text-2xl md:text-3xl mt-1.5 ${isDark ? "text-white" : "text-black"}`}>{t.title}</h3>
                  <p className={`mt-3 text-sm leading-relaxed ${isDark ? "text-white/75" : "text-[color:var(--c-grey-700)]"}`}>{t.desc}</p>
                  <ul className="mt-5 space-y-2">
                    {t.bullets.map(b => (
                      <li key={b} className="flex items-start gap-2.5 text-sm">
                        <i className="fa-solid fa-check mt-1 text-xs" style={{ color: isDark ? "var(--c-gold)" : "var(--c-gold-deep)" }}></i>
                        <span className={isDark ? "text-white/90" : ""}>{b}</span>
                      </li>
                    ))}
                  </ul>
                  {t.key === "pallet" && (
                    <div
                      className="mt-5 rounded-xl p-3 flex items-start gap-2.5"
                      style={{ background: "var(--c-cream)", border: "1px solid var(--c-gold)" }}
                      data-testid="pallet-net30-badge"
                    >
                      <i className="fa-solid fa-file-invoice-dollar mt-0.5" style={{ color: "var(--c-gold-deep)" }}></i>
                      <div className="text-xs leading-snug">
                        <div className="font-semibold text-black">Net 30 Terms Available</div>
                        <div className="text-[color:var(--c-grey-700)]">for approved trade customers</div>
                      </div>
                    </div>
                  )}
                  <div className="mt-6 pt-5" style={{ borderTop: `1px solid ${isDark ? "#2a2a28" : "var(--c-grey-100)"}` }}>
                    <Link to={t.to} className={`btn w-full ${idx === 1 ? "btn-red" : (isDark ? "btn-gold" : "btn-black")}`} data-testid={`${t.testid}-cta`}>
                      {t.cta} <i className="fa-solid fa-arrow-right"></i>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-10 flex flex-col md:flex-row items-center justify-center gap-4 text-sm text-[color:var(--c-grey-700)]">
          <span className="inline-flex items-center gap-2"><i className="fa-solid fa-circle-check" style={{ color: "var(--c-gold-deep)" }}></i> No minimum order on retail</span>
          <span className="hidden md:inline opacity-30">·</span>
          <span className="inline-flex items-center gap-2"><i className="fa-solid fa-circle-check" style={{ color: "var(--c-gold-deep)" }}></i> Mix retail &amp; box in one order</span>
          <span className="hidden md:inline opacity-30">·</span>
          <span className="inline-flex items-center gap-2"><i className="fa-solid fa-circle-check" style={{ color: "var(--c-gold-deep)" }}></i> Pallet quotes within 1 hour</span>
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  const items = [
    "FREE STANDARD UK DELIVERY OVER £50",
    "PACK OR FULL BOX — YOUR CHOICE",
    "TRADE ACCOUNTS WELCOME",
    "FIRST-TIME BUYER · CODE BY EMAIL",
    "BULK & PALLET ORDERS",
    "RETAIL & WHOLESALE",
  ];
  const loop = [...items, ...items];
  return (
    <div className="border-y" style={{ borderColor: "var(--c-grey-100)", background: "var(--c-black)" }}>
      <div className="overflow-hidden">
        <div className="marquee-track py-3">
          {loop.map((t, i) => (
            <div key={i} className="flex items-center gap-8 px-8 whitespace-nowrap">
              <span className="text-xs tracking-[0.22em] font-semibold" style={{ color: "var(--c-gold)" }}>{t}</span>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--c-gold-deep)" }}></span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* -------------------- Categories Section -------------------- */
function CategoryGrid() {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    axios.get(`${API}/categories`).then(r => setCats(r.data)).catch(() => {});
  }, []);
  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <SectionTitle eyebrow="Shop by Category" title="Everything for home, business & trade" subtitle="Quality British supply — browse by what you need." />
          <Link to="/shop" className="btn btn-outline-black self-start" data-testid="cat-view-all">View all <i className="fa-solid fa-arrow-right"></i></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6" data-testid="category-grid">
          {cats.map((c) => (
            <Link key={c.slug} to={`/shop?category=${encodeURIComponent(c.match)}`} className="group relative aspect-[4/3] rounded-2xl overflow-hidden lift" data-testid={`category-${c.slug}`}>
              {c.image ? (
                <img
                  src={c.image}
                  alt={c.name}
                  width="800"
                  height="600"
                  decoding="async"
                  fetchPriority="high"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              ) : (
                <div className="absolute inset-0" style={{ background: "var(--c-black)" }}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <i className="fa-solid fa-box-open text-5xl" style={{ color: "var(--c-gold)", opacity: 0.18 }}></i>
                  </div>
                </div>
              )}
              {/* Dark gradient for text legibility */}
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,10,0.20) 0%, rgba(10,10,10,0.55) 60%, rgba(10,10,10,0.85) 100%)" }} />
              <div className="absolute inset-0 p-5 md:p-6 flex flex-col justify-end text-white">
                <div className="text-[10px] md:text-xs font-semibold uppercase tracking-[0.18em] mb-1" style={{ color: "var(--c-gold)" }}>{c.tag}</div>
                <div className="font-display text-lg md:text-2xl leading-tight drop-shadow">{c.name}</div>
                <div className="mt-2 inline-flex items-center gap-2 text-xs md:text-sm opacity-95 group-hover:opacity-100">
                  Shop now <i className="fa-solid fa-arrow-right transition-transform group-hover:translate-x-1"></i>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------- Product Card -------------------- */
function ProductCard({ p }) {
  const { add } = useCart();
  // Bundle-tagged Shopify products render with a premium presentation.
  if (isBundleProduct(p)) {
    return <BundleCard p={p} useCart={useCart} />;
  }
  // Choose default variant: first available, else first
  const defaultVariant = p.variants?.find(v => v.available) || p.variants?.[0];
  const hasMultipleVariants = (p.variants?.length || 0) > 1;
  const isOnSale = defaultVariant?.compare_at_price && defaultVariant.compare_at_price > defaultVariant.price;
  const priceText = p.min_price === p.max_price
    ? `£${p.min_price.toFixed(2)}`
    : `from £${p.min_price.toFixed(2)}`;

  return (
    <div className="card-product flex flex-col" data-testid={`product-card-${p.slug}`}>
      <Link to={`/product/${p.slug}`} className="relative block aspect-square overflow-hidden bg-[color:var(--c-grey-50)]">
        {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" /> : <div className="w-full h-full flex items-center justify-center text-[color:var(--c-grey-200)]"><i className="fa-solid fa-image text-4xl"></i></div>}
        {isOnSale && (
          <span className="absolute top-3 left-3 badge-pill text-white" style={{ background: "var(--c-red)" }}>
            <i className="fa-solid fa-tag text-[9px]"></i> Sale
          </span>
        )}
        {p.bestseller && (
          <span className="absolute top-3 right-3 badge-pill" style={{ background: "var(--c-gold)", color: "var(--c-black)" }}>
            <i className="fa-solid fa-crown text-[9px]"></i> Best Seller
          </span>
        )}
        {!p.available && (
          <span className="absolute bottom-3 left-3 badge-pill text-white" style={{ background: "var(--c-grey-700)" }}>Sold Out</span>
        )}
      </Link>
      <div className="p-4 md:p-5 flex flex-col flex-1">
        <div className="text-[11px] uppercase tracking-[0.14em] mb-1.5" style={{ color: "var(--c-gold-deep)" }}>{p.category}</div>
        <Link to={`/product/${p.slug}`} className="font-display text-base md:text-lg leading-snug text-black hover:underline line-clamp-2">{p.name}</Link>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="font-display text-2xl text-black">{priceText}</span>
          {isOnSale && (
            <span className="text-sm text-[color:var(--c-grey-500)] line-through">£{defaultVariant.compare_at_price.toFixed(2)}</span>
          )}
        </div>
        {hasMultipleVariants && (
          <div className="mt-1 text-xs text-[color:var(--c-grey-500)]">{p.variants.length} sizes/options available</div>
        )}
        <div className="mt-3 grid grid-cols-1 gap-2">
          {hasMultipleVariants ? (
            <Link to={`/product/${p.slug}`} className="btn btn-black !py-2.5 !text-xs" data-testid={`view-options-${p.slug}`}>
              Choose options <i className="fa-solid fa-arrow-right"></i>
            </Link>
          ) : (
            <button onClick={() => add(p, defaultVariant)} disabled={!p.available} className="btn btn-red !py-2.5 !text-xs disabled:opacity-50" data-testid={`add-to-cart-${p.slug}`}>
              <i className="fa-solid fa-bag-shopping"></i> Add to Cart
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------- Best Sellers -------------------- */
function BestSellers() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/products?bestseller=true`).then(r => setItems(r.data)).catch(() => {});
  }, []);
  return (
    <section className="py-16 md:py-24" style={{ background: "var(--c-cream)" }}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <SectionTitle eyebrow="Best Sellers" title="The products keeping UK businesses moving" subtitle="Real demand. Real stock. Real value." />
          <Link to="/shop?bestseller=true" className="btn btn-black self-start" data-testid="bestsellers-view-all">All best sellers <i className="fa-solid fa-arrow-right"></i></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" data-testid="bestsellers-grid">
          {items.slice(0, 4).map(p => <ProductCard key={p.id} p={p} />)}
        </div>
      </div>
    </section>
  );
}

/* -------------------- Why Choose Us -------------------- */
function WhyChoose() {
  const features = [
    { i: "fa-truck-fast", t: "Fast UK Delivery", d: "Nationwide same-day dispatch from our UK warehouse." },
    { i: "fa-pound-sign", t: "Competitive Pricing", d: "Buy a pack or save more with full box quantities." },
    { i: "fa-shield-halved", t: "Secure Checkout", d: "Visa, Mastercard, PayPal — all major payments." },
    { i: "fa-headset", t: "Live Chat Support", d: "Friendly UK-based team ready to help in real time." },
    { i: "fa-warehouse", t: "Real Stock, Real Warehouse", d: "Genuine product photography from our UK facility." },
    { i: "fa-store", t: "Retail & Trade Welcome", d: "From single packs to pallets — flexible ordering." },
  ];
  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <SectionTitle eyebrow="Why Crown01" title="Reliable, professional, value-driven." subtitle="A UK supplier trusted by households, retailers and trade customers nationwide." align="center" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mt-12">
          {features.map(f => (
            <div key={f.t} className="p-6 md:p-7 rounded-2xl lift" style={{ background: "var(--c-grey-50)", border: "1px solid var(--c-grey-100)" }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: "var(--c-black)" }}>
                <i className={`fa-solid ${f.i} text-lg`} style={{ color: "var(--c-gold)" }}></i>
              </div>
              <div className="font-display text-xl text-black">{f.t}</div>
              <p className="mt-2 text-sm text-[color:var(--c-grey-700)] leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------- Bulk CTA -------------------- */
function BulkBanner() {
  return (
    <section className="py-16 md:py-24" style={{ background: "var(--c-black)" }}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] mb-4" style={{ color: "var(--c-gold)" }}>
            <i className="fa-solid fa-truck-ramp-box"></i> Pallet & Bulk Orders
          </div>
          <h2 className="font-display text-3xl md:text-5xl text-white leading-tight">
            Need <span className="text-gradient-gold">trade quantities</span>?<br />Get a custom quote in 1 business hour.
          </h2>
          <p className="mt-5 text-white/75 text-base md:text-lg max-w-xl">
            From restaurants and cleaning contractors to large retailers — we offer competitive pricing, custom delivery rates, and dedicated account management for bulk and pallet orders.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/trade" className="btn btn-gold" data-testid="bulk-quote-btn">Request a Quote <i className="fa-solid fa-arrow-right"></i></Link>
            <a href="tel:+440000000000" className="btn btn-ghost text-white hover:bg-white/10">
              <i className="fa-solid fa-phone"></i> Speak to a trade rep
            </a>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/70">
            <span><i className="fa-solid fa-check mr-2" style={{ color: "var(--c-gold)" }}></i> Pallet pricing</span>
            <span><i className="fa-solid fa-check mr-2" style={{ color: "var(--c-gold)" }}></i> Net 30 trade terms</span>
            <span><i className="fa-solid fa-check mr-2" style={{ color: "var(--c-gold)" }}></i> Same-day dispatch</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <img src="https://e7qcac-xy.myshopify.com/cdn/shop/files/IMG_6978_2.jpg?v=1771961263&width=600" className="rounded-2xl aspect-[4/5] object-cover" alt="Warehouse" />
          <div className="grid gap-4">
            <img src="https://images.unsplash.com/photo-1601598851547-4302969d0614?w=800&q=80" className="rounded-2xl aspect-square object-cover" alt="Pallets" />
            <div className="rounded-2xl p-6" style={{ background: "var(--c-ink)", border: "1px solid #2a2a28" }}>
              <div className="font-display text-3xl" style={{ color: "var(--c-gold)" }}>40%+</div>
              <div className="text-sm text-white/70 mt-1">Average bulk vs. pack savings</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Proven Market Performance --------------------
 * Replaces the previous "Trusted by UK households & businesses" section.
 * No fake reviews — only real, verifiable marketplace performance metrics
 * sourced from our live Amazon / eBay / TikTok Shop listings.
 * ---------------------------------------------------------------------- */
function ProvenMarketPerformance() {
  return (
    <section className="py-16 md:py-24" style={{ background: "var(--c-grey-50)" }} data-testid="proven-market-performance">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="grid md:grid-cols-[1fr_1fr] gap-6 md:gap-12 items-end">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-semibold" style={{ color: "var(--c-gold-deep)" }}>Verified by end customers</div>
            <h2 className="font-display text-3xl md:text-5xl text-black mt-3 leading-[1.05]">Proven Market Performance</h2>
            <div className="mt-4 h-[3px] w-16" style={{ background: "var(--c-gold)" }}></div>
          </div>
          <p className="text-sm md:text-base text-[color:var(--c-grey-700)] leading-relaxed md:text-right">
            We sell the same products direct to consumers on <strong className="text-black">eBay, Amazon and TikTok Shop</strong> — and Crown01 is enrolled in <strong className="text-black">Amazon Business (B2B)</strong>, so business buyers can also purchase with business pricing. Real listings and verified reviews below.
          </p>
        </div>

        {/* Catalogue screenshot — single hero visual */}
        <div className="mt-10 rounded-2xl overflow-hidden shadow-lg" style={{ background: "#fff", border: "1px solid var(--c-grey-100)" }} data-testid="proven-catalogue-image">
          <img
            src="https://customer-assets.emergentagent.com/job_better-site-15/artifacts/5k6gawhx_Screenshot%202026-06-16%20at%2022.19.48.png"
            alt="Crown01 verified marketplace listings, verified customer reviews across eBay, Amazon and TikTok Shop, and Amazon Business enrolment — proven market performance with a 4.7-star average rating"
            className="w-full h-auto block"
            loading="lazy"
          />
        </div>

        {/* Metric tiles — real numbers, no star ratings */}
        <div className="mt-8 md:mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-testid="proven-metrics">
          {[
            { num: "4.7★", lbl: "Listing ratings across our marketplace channels" },
            { num: "36,000+", lbl: "Units sold on a single refuse-sack listing alone" },
            { num: "Amazon B2B", lbl: "Enrolled in Amazon Business — business pricing for B2B buyers" },
            { num: "3 channels", lbl: "Live on Amazon, eBay and TikTok Shop today" },
          ].map((m, i) => (
            <div key={i} className="rounded-2xl p-5 md:p-6 bg-white" style={{ border: "1px solid var(--c-grey-100)" }} data-testid={`proven-metric-${i}`}>
              <div className="h-[2px] w-10 mb-3" style={{ background: "var(--c-gold)" }}></div>
              <div className="font-display text-2xl md:text-3xl text-black leading-none">{m.num}</div>
              <div className="mt-3 text-[11px] md:text-xs uppercase tracking-[0.14em] font-semibold text-[color:var(--c-grey-700)] leading-snug">{m.lbl}</div>
            </div>
          ))}
        </div>

        {/* CTAs back into the marketplace channels */}
        <div className="mt-8 md:mt-10 flex flex-wrap items-center justify-center gap-3">
          <a href={SOCIAL_LINKS.amazon.url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-black !text-xs md:!text-sm" data-testid="proven-cta-amazon">
            <BrandLogo name="Amazon" size={16} /> View Amazon listings
          </a>
          <a href={SOCIAL_LINKS.ebay.url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-black !text-xs md:!text-sm" data-testid="proven-cta-ebay">
            <BrandLogo name="eBay" size={16} /> View eBay listings
          </a>
          <a href={SOCIAL_LINKS.tiktok.url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-black !text-xs md:!text-sm" data-testid="proven-cta-tiktok">
            <BrandLogo name="TikTok Shop" size={16} /> View TikTok Shop
          </a>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Newsletter -------------------- */
function Newsletter() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { identifyCart } = useCart();
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/newsletter`, { email });
      toast.success("Welcome to Crown01!", { description: r.data.message });
      try { trackSignUp("newsletter"); } catch (e) { /* noop */ }
      await identifyCart(email, { silent: true });
      setEmail("");
    } catch (e) {
      toast.error("Could not subscribe. Try again.");
    } finally { setLoading(false); }
  };
  return (
    <section className="py-16 md:py-20" style={{ background: "var(--c-cream)" }}>
      <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-5" style={{ background: "var(--c-black)", color: "var(--c-gold)" }}>
          <i className="fa-solid fa-gift"></i> 5% Off Your First Order
        </div>
        <h2 className="font-display text-3xl md:text-5xl text-black leading-tight">Join the Crown01 community.</h2>
        <p className="mt-4 text-[color:var(--c-grey-700)] max-w-xl mx-auto">Sign up for exclusive trade deals, restocks, and product updates. Unsubscribe anytime.</p>
        <form onSubmit={submit} className="mt-7 flex flex-col sm:flex-row max-w-lg mx-auto gap-3" data-testid="newsletter-form">
          <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="input-bare flex-1" data-testid="newsletter-input" />
          <button disabled={loading} className="btn btn-red" data-testid="newsletter-submit">
            {loading ? "Sending..." : (<>Get 5% Off <i className="fa-solid fa-arrow-right"></i></>)}
          </button>
        </form>
        <div className="mt-5 text-xs text-[color:var(--c-grey-500)]">We'll email your discount code · No spam, unsubscribe anytime</div>
      </div>
    </section>
  );
}

/* -------------------- Footer -------------------- */
function Footer() {
  return (
    <footer style={{ background: "var(--c-black)", color: "#fff" }} data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 grid md:grid-cols-2 lg:grid-cols-6 gap-10">
        <div className="lg:col-span-2">
          <Logo />
          <p className="mt-5 text-sm text-white/70 max-w-sm">
            Crown01 is the trading name of <strong className="text-white">NAR London Ltd</strong>, a trusted UK supplier of packaging, cleaning, catering and wholesale consumable products. Buy one pack, one box, or one pallet — the choice is yours.
          </p>

          {/* Company / address card */}
          <div className="mt-6 p-4 rounded-xl text-xs leading-relaxed" style={{ background: "#1a1a1a", border: "1px solid #2a2a28" }} data-testid="footer-company-info">
            <div className="uppercase tracking-[0.18em] font-semibold mb-2" style={{ color: "var(--c-gold)" }}>Company &amp; Warehouse</div>
            <div className="text-white/80">
              <strong className="text-white">NAR London Ltd</strong> · trading as <strong className="text-white">Crown01</strong><br />
              95 Theobalds Park Road<br />
              Enfield, EN2 9BS, United Kingdom<br />
              <a href="mailto:info@crown01.co.uk" className="underline" style={{ color: "var(--c-gold)" }}>info@crown01.co.uk</a>
            </div>
          </div>

          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-2.5" style={{ color: "var(--c-gold)" }}>Follow us for offers &amp; new products</div>
            <div className="flex items-center gap-2.5" data-testid="footer-socials">
              {[SOCIAL_LINKS.instagram, SOCIAL_LINKS.facebook, SOCIAL_LINKS.linkedin, SOCIAL_LINKS.tiktok].map(s => (
                <a key={s.name} href={s.url} target={s.url === "#" ? "_self" : "_blank"} rel="noopener noreferrer" aria-label={s.name} className="w-10 h-10 rounded-full flex items-center justify-center transition hover:scale-110" style={{ background: "#1a1a1a", border: "1px solid #2a2a28" }} data-testid={`footer-social-${s.name.toLowerCase()}`}>
                  <i className={s.icon} style={{ color: s.color === "var(--c-black)" ? "var(--c-gold)" : s.color }}></i>
                </a>
              ))}
            </div>
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.18em] mb-4" style={{ color: "var(--c-gold)" }}>Shop</div>
          <ul className="space-y-2.5 text-sm text-white/75">
            <li><Link to="/shop">All Products</Link></li>
            <li><Link to="/shop?bestseller=true">Best Sellers</Link></li>
            <li><Link to="/shop?category=bin-bags-and-refuse-sacks">Bin Bags</Link></li>
            <li><Link to="/shop?category=carrier-bags-shopping-bags">Carrier Bags</Link></li>
            <li><Link to="/shop?category=soft-serviettes-napkins">Serviettes &amp; Napkins</Link></li>
            <li><Link to="/shop?category=view-our-catering-products">Catering</Link></li>
            <li><Link to="/trade">Trade &amp; Wholesale</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.18em] mb-4" style={{ color: "var(--c-gold)" }}>Customer</div>
          <ul className="space-y-2.5 text-sm text-white/75" data-testid="footer-customer-links">
            <li><Link to="/faq" data-testid="footer-link-faq">FAQ</Link></li>
            <li><Link to="/shipping" data-testid="footer-link-shipping">Shipping &amp; Delivery</Link></li>
            <li><Link to="/returns" data-testid="footer-link-returns">Returns &amp; Refunds</Link></li>
            <li><Link to="/contact" data-testid="footer-link-contact">Contact Us</Link></li>
            <li><Link to="/wholesale" data-testid="footer-link-wholesale-info">Trade &amp; Wholesale Info</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.18em] mb-4" style={{ color: "var(--c-gold)" }}>Company</div>
          <ul className="space-y-2.5 text-sm text-white/75" data-testid="footer-company-links">
            <li><Link to="/about" data-testid="footer-link-about">About Us</Link></li>
            <li><Link to="/careers" data-testid="footer-link-careers">Careers</Link></li>
            <li><Link to="/trade">Apply for Trade Pricing</Link></li>
            <li><a href={SOCIAL_LINKS.amazon.url} target="_blank" rel="noopener noreferrer"><i className="fa-brands fa-amazon mr-2" style={{ color: SOCIAL_LINKS.amazon.color }}></i>Amazon</a></li>
            <li><a href={SOCIAL_LINKS.tiktok.url} target="_blank" rel="noopener noreferrer"><i className="fa-brands fa-tiktok mr-2" style={{ color: "#fff" }}></i>TikTok Shop</a></li>
            <li><a href={SOCIAL_LINKS.ebay.url} target="_blank" rel="noopener noreferrer"><i className="fa-brands fa-ebay mr-2" style={{ color: SOCIAL_LINKS.ebay.color }}></i>eBay</a></li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.18em] mb-4" style={{ color: "var(--c-gold)" }}>Legal</div>
          <ul className="space-y-2.5 text-sm text-white/75" data-testid="footer-legal-links">
            <li><Link to="/privacy" data-testid="footer-link-privacy">Privacy Policy</Link></li>
            <li><Link to="/terms" data-testid="footer-link-terms">Terms &amp; Conditions</Link></li>
            <li><Link to="/cookies" data-testid="footer-link-cookies">Cookie Policy</Link></li>
            <li><Link to="/returns">Refund Policy</Link></li>
            <li>
              <button
                type="button"
                onClick={() => { if (typeof window.openCookiePreferences === "function") window.openCookiePreferences(); }}
                className="text-left hover:underline"
                data-testid="footer-link-cookie-preferences"
              >
                Cookie Preferences
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t" style={{ borderColor: "#1a1a1a" }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/55">
          <div>© {new Date().getFullYear()} <strong className="text-white/80">NAR London Ltd</strong> trading as Crown01. All rights reserved. Registered in the United Kingdom.</div>
          <div className="flex items-center gap-4">
            <i className="fa-brands fa-cc-visa text-xl"></i>
            <i className="fa-brands fa-cc-mastercard text-xl"></i>
            <i className="fa-brands fa-cc-paypal text-xl"></i>
            <i className="fa-brands fa-cc-amex text-xl"></i>
            <i className="fa-brands fa-apple-pay text-2xl"></i>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* -------------------- Pages -------------------- */

/* -------------------- Home Bundle Deals -------------------- */
/** Home-page entry point to the bundle catalogue.
 *  - If Shopify has at least one product tagged `bundle`, it surfaces them
 *    inside the same premium BundleDealsSection used on /shop.
 *  - If no bundles exist yet, a slim teaser CTA renders so visitors still
 *    have a discoverable path to the bundles page (which is empty until
 *    you publish your first bundle). */
function HomeBundleDeals() {
  const [bundles, setBundles] = useState([]);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    // Server-side Shopify `tag:bundle` filter — only returns published bundle products.
    axios.get(`${API}/products`, { params: { tag: "bundle" } })
      .then(r => setBundles(Array.isArray(r.data) ? r.data : []))
      .catch(() => setBundles([]))
      .finally(() => setReady(true));
  }, []);
  if (!ready) return null;

  // Real bundles exist → use the same premium section as /shop
  if (bundles.length > 0) {
    return <BundleDealsSection bundles={bundles} useCart={useCart} />;
  }

  // Fallback teaser (rendered until at least one Shopify product is tagged `bundle`)
  return (
    <section className="py-12 md:py-16" style={{ background: "linear-gradient(180deg, var(--c-cream) 0%, #fefdfb 100%)" }} data-testid="home-bundles-teaser">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div
          className="rounded-2xl p-6 md:p-10 grid md:grid-cols-[1.4fr_1fr] gap-6 items-center"
          style={{ background: "var(--c-black)", color: "#fff", border: "1px solid var(--c-gold)" }}
        >
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em]" style={{ background: "rgba(196,155,80,0.18)", color: "var(--c-gold)" }}>
              <i className="fa-solid fa-gem text-[9px]" /> Bundle deals
            </div>
            <h2 className="font-display text-2xl md:text-4xl text-white mt-3 leading-tight">
              Save more when you buy your essentials together.
            </h2>
            <p className="mt-3 text-sm md:text-base text-white/75 max-w-xl">
              Hand-picked combinations of our top-selling refuse sacks, catering supplies and household essentials —
              <strong className="text-white"> priced lower than buying each item individually</strong>. Trade and household packs available.
            </p>
            <Link
              to="/shop?tag=bundle"
              className="btn btn-gold mt-5 inline-flex items-center gap-2"
              data-testid="home-bundles-cta"
            >
              <i className="fa-solid fa-bag-shopping" /> View Bundle Deals
              <i className="fa-solid fa-arrow-right text-xs" />
            </Link>
          </div>
          <div className="hidden md:grid grid-cols-2 gap-3">
            {[
              { tag: "Save 5%", line: "Takeaway & Catering Essentials" },
              { tag: "Save 7%", line: "Refuse Sack Discovery Bundle" },
            ].map(b => (
              <div key={b.tag} className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(196,155,80,0.25)" }}>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: "var(--c-gold)" }}>{b.tag}</div>
                <div className="font-display text-sm text-white mt-1 leading-snug">{b.line}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function HomePage() {
  return (
    <>
      <SEO
        title="Whether You Need 1 Pack or 1 Pallet"
        description="Trusted UK supplier of packaging, cleaning & trade products. Single packs, full boxes, wholesale and pallet orders. Fast UK delivery, real warehouse stock."
        path="/"
      />
      <Hero />
      <ShopYourWay />
      <CategoryGrid />
      <HomeBundleDeals />
      <BestSellers />
      <WhyChoose />
      <BulkBanner />
      <ProvenMarketPerformance />
      <InsideCrown01 />
      <TrustCommunity />
      <Newsletter />
    </>
  );
}

function useQueryParams() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function ShopPage() {
  const qp = useQueryParams();
  const category = qp.get("category");
  const bestseller = qp.get("bestseller");
  const search = qp.get("search");
  const tag = qp.get("tag");  // e.g. ?tag=bundle
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [sort, setSort] = useState("featured");
  const [priceBand, setPriceBand] = useState("all");  // all | under5 | 5-15 | 15-50 | 50plus
  const [buyType, setBuyType] = useState("all");      // all | pack | box
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false); // mobile filter drawer
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    if (bestseller) params.bestseller = true;
    if (search) params.search = search;
    if (tag) params.tag = tag;   // server-side Shopify "tag:" filter
    axios.get(`${API}/products`, { params }).then(r => { setItems(r.data); setLoading(false); });
    axios.get(`${API}/categories`).then(r => setCats(r.data));
  }, [category, bestseller, search, tag]);

  // Reset price/buy filters when nav changes
  useEffect(() => { setPriceBand("all"); setBuyType("all"); }, [category, bestseller, search]);

  // Close mobile filter drawer on route change
  useEffect(() => { setFiltersOpen(false); }, [category, bestseller, search]);

  // Lock scroll while mobile drawer is open
  useEffect(() => {
    if (filtersOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = original; };
    }
  }, [filtersOpen]);

  const sorted = useMemo(() => {
    let arr = [...items];
    // Tag filter (e.g. ?tag=bundle for the dedicated bundle page)
    if (tag) {
      const t = String(tag).toLowerCase().trim();
      arr = arr.filter(p => Array.isArray(p.tags) && p.tags.some(x => String(x).toLowerCase().trim() === t));
    }
    if (priceBand !== "all") {
      arr = arr.filter(p => {
        const low = p.min_price;
        const high = p.max_price;
        if (priceBand === "under5") return low < 5;
        if (priceBand === "5-15") return (low >= 5 && low <= 15) || (high >= 5 && high <= 15);
        if (priceBand === "15-50") return (low >= 15 && low <= 50) || (high >= 15 && high <= 50);
        if (priceBand === "50plus") return high >= 50;
        return true;
      });
    }
    if (sort === "price-asc") arr.sort((a,b) => a.min_price - b.min_price);
    if (sort === "price-desc") arr.sort((a,b) => b.min_price - a.min_price);
    if (sort === "rating") arr.sort((a,b) => b.rating - a.rating);
    if (sort === "name") arr.sort((a,b) => a.name.localeCompare(b.name));
    // Bundles must never dominate the top of /shop. Unless the visitor is
    // explicitly on the bundles tab (?tag=bundle), stable-partition the
    // result so individual products render first and bundle-tagged
    // products are pushed to the bottom of the grid (still discoverable
    // by scrolling, never lost from the catalogue).
    if (tag !== "bundle") {
      const nonBundles = arr.filter(p => !isBundleProduct(p));
      const bundlesAtEnd = arr.filter(p => isBundleProduct(p));
      arr = [...nonBundles, ...bundlesAtEnd];
    }
    return arr;
  }, [items, sort, priceBand, tag]);

  // Resolve a slug URL back to the human-readable collection name for the page title.
  const matchedCat = cats.find(c => c.slug === category || c.match === category);
  const categoryDisplayName = matchedCat?.name || category;
  const pageTitle = tag === "bundle" ? "Bundle Deals" : search ? `Search: "${search}"` : category ? categoryDisplayName : bestseller ? "Best Sellers" : "All Products";
  const seoPath = tag ? `/shop?tag=${encodeURIComponent(tag)}` : search ? `/shop?search=${encodeURIComponent(search)}` : category ? `/shop?category=${encodeURIComponent(category)}` : bestseller ? "/shop?bestseller=true" : "/shop";
  const hasActiveFilters = priceBand !== "all" || buyType !== "all";
  const activeFilterCount = (priceBand !== "all" ? 1 : 0) + (buyType !== "all" ? 1 : 0);

  // Shared filter content (used in sidebar on desktop + drawer on mobile)
  const renderFilters = (compact = false) => (
    <div className={compact ? "space-y-6" : "space-y-7"} data-testid="shop-filters">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] mb-3" style={{ color: "var(--c-gold-deep)" }}>Categories</div>
        <ul className="space-y-1">
          <li>
            <button onClick={() => { navigate("/shop"); setFiltersOpen(false); }} className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${!category && !bestseller && !search ? "bg-black text-white" : "hover:bg-[color:var(--c-grey-50)]"}`} data-testid="filter-all">
              All Products
            </button>
          </li>
          <li>
            <button onClick={() => { navigate("/shop?bestseller=true"); setFiltersOpen(false); }} className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${bestseller ? "bg-black text-white" : "hover:bg-[color:var(--c-grey-50)]"}`} data-testid="filter-bestseller">
              <i className="fa-solid fa-crown mr-2" style={{ color: bestseller ? "var(--c-gold)" : "var(--c-gold-deep)" }}></i>
              Best Sellers
            </button>
          </li>
          {cats.map(c => {
            const active = category === c.match || category === c.slug;
            return (
            <li key={c.slug}>
              <button onClick={() => { navigate(`/shop?category=${encodeURIComponent(c.slug)}`); setFiltersOpen(false); }} className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${active ? "bg-black text-white" : "hover:bg-[color:var(--c-grey-50)]"}`} data-testid={`filter-cat-${c.slug}`}>
                {c.name}
              </button>
            </li>
            );
          })}
        </ul>
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.18em] mb-3" style={{ color: "var(--c-gold-deep)" }}>Price</div>
        <div className="space-y-1.5" data-testid="price-band-filter">
          {[
            { v: "all", l: "Any price" },
            { v: "under5", l: "Under £5" },
            { v: "5-15", l: "£5 – £15" },
            { v: "15-50", l: "£15 – £50" },
            { v: "50plus", l: "£50+" },
          ].map(b => (
            <label key={b.v} className={`flex items-center gap-2.5 px-3 py-1.5 rounded-lg cursor-pointer transition text-sm ${priceBand === b.v ? "bg-[color:var(--c-cream)]" : "hover:bg-[color:var(--c-grey-50)]"}`} data-testid={`price-${b.v}`}>
              <input type="radio" name="price" value={b.v} checked={priceBand === b.v} onChange={() => setPriceBand(b.v)} className="accent-[color:var(--c-black)]" />
              <span>{b.l}</span>
            </label>
          ))}
        </div>
      </div>

      {hasActiveFilters && (
        <button onClick={() => { setPriceBand("all"); setBuyType("all"); }} className="w-full text-xs text-[color:var(--c-grey-500)] underline" data-testid="clear-filters">
          Clear filters
        </button>
      )}

      <div className="p-5 rounded-2xl" style={{ background: "var(--c-black)", color: "#fff" }}>
        <div className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--c-gold)" }}>Need bulk?</div>
        <div className="font-display text-xl mt-1">Pallet pricing available</div>
        <Link to="/trade" className="mt-3 btn btn-gold !text-xs !py-2 !px-4 w-full" onClick={() => setFiltersOpen(false)}>Get a quote</Link>
      </div>
    </div>
  );

  return (
    <div className="bg-white">
      <SEO
        title={pageTitle === "All Products" ? "Shop All — Single Packs, Full Boxes, Wholesale" : `Shop ${pageTitle}`}
        description={`Shop ${pageTitle.toLowerCase()} at Crown01 — UK supplier of packaging, cleaning and trade products. Buy single packs or full boxes with fast UK delivery.`}
        path={seoPath}
      />
      {/* Slim page header */}
      <div className="border-b" style={{ borderColor: "var(--c-grey-100)" }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-6 md:pt-8 pb-4 md:pb-5">
          <div className="text-[11px] uppercase tracking-[0.18em] mb-1.5" style={{ color: "var(--c-gold-deep)" }}>
            <Link to="/" className="hover:underline">Home</Link> · Shop
          </div>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="font-display text-2xl md:text-4xl text-black leading-tight" data-testid="shop-title">{pageTitle}</h1>
              <p className="mt-1 text-xs md:text-sm text-[color:var(--c-grey-500)]">{sorted.length} product{sorted.length !== 1 ? "s" : ""}{hasActiveFilters ? " (filtered)" : ""}</p>
            </div>
            {/* Quick category chips — only on home of Shop, not on a single-category page */}
            {!category && !bestseller && !search && cats.length > 0 && (
              <div className="hidden md:flex flex-wrap items-center gap-2" data-testid="quick-category-chips">
                {cats.slice(0, 5).map(c => (
                  <button key={c.slug} onClick={() => navigate(`/shop?category=${encodeURIComponent(c.match)}`)} className="px-3 py-1.5 rounded-full text-xs font-semibold transition" style={{ background: "var(--c-grey-50)", border: "1px solid var(--c-grey-100)", color: "var(--c-grey-700)" }}>
                    {c.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sticky filter & sort bar */}
      <div className="sticky top-[64px] md:top-[80px] z-30 border-b backdrop-blur" style={{ borderColor: "var(--c-grey-100)", background: "rgba(255,255,255,0.92)" }} data-testid="shop-toolbar">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-2.5 md:py-3 flex items-center gap-2 md:gap-3 flex-wrap">
          {/* Mobile: filter button */}
          <button
            onClick={() => setFiltersOpen(true)}
            className="lg:hidden inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold"
            style={{ background: "var(--c-black)", color: "#fff" }}
            data-testid="mobile-filters-btn"
            aria-haspopup="dialog"
          >
            <i className="fa-solid fa-sliders"></i> Filter &amp; Sort
            {activeFilterCount > 0 && (
              <span className="ml-1 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ background: "var(--c-gold)", color: "var(--c-black)" }} data-testid="mobile-filters-badge">
                {activeFilterCount}
              </span>
            )}
          </button>

          {/* Desktop: inline price chips */}
          <div className="hidden lg:flex items-center gap-2" data-testid="toolbar-price-chips">
            <span className="text-[11px] uppercase tracking-wider text-[color:var(--c-grey-500)] mr-1">Price</span>
            {[
              { v: "all", l: "Any" },
              { v: "under5", l: "Under £5" },
              { v: "5-15", l: "£5–£15" },
              { v: "15-50", l: "£15–£50" },
              { v: "50plus", l: "£50+" },
            ].map(b => (
              <button
                key={b.v}
                onClick={() => setPriceBand(b.v)}
                className="px-3 py-1.5 rounded-full text-xs font-semibold transition"
                style={priceBand === b.v
                  ? { background: "var(--c-black)", color: "#fff" }
                  : { background: "var(--c-grey-50)", border: "1px solid var(--c-grey-100)", color: "var(--c-grey-700)" }}
                data-testid={`toolbar-price-${b.v}`}
              >
                {b.l}
              </button>
            ))}
          </div>

          {hasActiveFilters && (
            <button onClick={() => { setPriceBand("all"); setBuyType("all"); }} className="hidden lg:inline-block text-xs text-[color:var(--c-grey-500)] underline ml-1" data-testid="toolbar-clear-filters">
              Clear
            </button>
          )}

          <div className="ml-auto flex items-center gap-2">
            <label className="text-[11px] uppercase tracking-wider text-[color:var(--c-grey-500)]">Sort</label>
            <select value={sort} onChange={e => setSort(e.target.value)} className="input-box !py-1.5 !px-3 !rounded-full !w-auto text-xs md:text-sm" data-testid="shop-sort">
              <option value="featured">Featured</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="rating">Top rated</option>
              <option value="name">Name A–Z</option>
            </select>
          </div>
        </div>
      </div>

      {/* Body: sidebar (desktop only) + products */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10 grid lg:grid-cols-[240px_1fr] gap-10">
        <aside className="hidden lg:block lg:sticky lg:top-[148px] self-start" data-testid="shop-sidebar">
          {renderFilters(true)}
        </aside>

        {/* Main products area — first thing the visitor sees */}
        <div>
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-square bg-[color:var(--c-grey-100)] rounded-xl"></div>
                  <div className="h-4 mt-3 bg-[color:var(--c-grey-100)] rounded w-3/4"></div>
                  <div className="h-3 mt-2 bg-[color:var(--c-grey-100)] rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : sorted.length === 0 ? (
            tag === "bundle" ? (
              // No bundle-tagged products published in Shopify yet → clean
              // empty state. The moment a product is tagged `bundle` and
              // published to the Online Store sales channel in Shopify Admin,
              // it appears here automatically.
              <div className="text-center py-16 max-w-xl mx-auto" data-testid="bundles-empty">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-5" style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.35)" }}>
                  <i className="fa-solid fa-gem text-2xl" style={{ color: "var(--c-gold-deep)" }} aria-hidden="true"></i>
                </div>
                <h2 className="font-display text-2xl md:text-3xl text-black leading-tight" data-testid="bundles-empty-title">Bundle deals coming soon</h2>
                <p className="mt-3 text-sm md:text-base text-[color:var(--c-grey-500)] leading-relaxed">
                  We&apos;re curating multi-pack savings on our most popular catering and household lines. In the meantime, browse single packs and full boxes site-wide — or talk to our trade desk about bespoke pricing.
                </p>
                <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/shop" className="btn btn-red" data-testid="bundles-empty-shop">
                    Shop all products <i className="fa-solid fa-arrow-right text-xs ml-1"></i>
                  </Link>
                  <Link to="/trade" className="btn btn-outline-black" data-testid="bundles-empty-trade">
                    <i className="fa-solid fa-handshake mr-1"></i> Talk to the trade desk
                  </Link>
                </div>
              </div>
            ) : (
              <div className="text-center py-16">
                <i className="fa-solid fa-magnifying-glass text-3xl text-[color:var(--c-grey-200)]"></i>
                <p className="mt-4 text-[color:var(--c-grey-500)]">No products match your filters.</p>
                <button onClick={() => { setPriceBand("all"); setBuyType("all"); }} className="btn btn-outline-black mt-4">Clear filters</button>
              </div>
            )
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-testid="shop-grid">
              {sorted.map(p => <ProductCard key={p.id} p={p} />)}
            </div>
          )}
        </div>
      </div>

      {/* Mobile filters drawer */}
      {filtersOpen && (
        <div className="lg:hidden fixed inset-0 z-[80] flex" role="dialog" aria-label="Filter & Sort" data-testid="mobile-filters-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setFiltersOpen(false)} />
          <div className="relative ml-auto w-[88%] max-w-sm h-full bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "var(--c-grey-100)" }}>
              <div className="font-display text-lg text-black flex items-center gap-2">
                <i className="fa-solid fa-sliders" style={{ color: "var(--c-gold-deep)" }}></i> Filter &amp; Sort
              </div>
              <button onClick={() => setFiltersOpen(false)} className="w-9 h-9 rounded-full bg-[color:var(--c-grey-50)] flex items-center justify-center" aria-label="Close" data-testid="mobile-filters-close">
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5">
              {renderFilters(true)}
            </div>
            <div className="px-5 py-4 border-t flex gap-2" style={{ borderColor: "var(--c-grey-100)", background: "var(--c-grey-50)" }}>
              <button onClick={() => { setPriceBand("all"); setBuyType("all"); }} className="btn btn-outline-black !py-2.5 !text-xs flex-1">Clear</button>
              <button onClick={() => setFiltersOpen(false)} className="btn btn-red !py-2.5 !text-xs flex-1" data-testid="mobile-filters-apply">
                Show {sorted.length} product{sorted.length !== 1 ? "s" : ""}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProductPage() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [qty, setQty] = useState(1);
  const [active, setActive] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [related, setRelated] = useState([]);
  const { add } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/products/${slug}`).then(r => {
      setP(r.data);
      setActive(0);
      setQty(1);
      const v = r.data.variants?.find(v => v.available) || r.data.variants?.[0];
      setSelectedVariantId(v?.id || null);
      try { trackViewItem(r.data, v); } catch (e) { /* noop */ }

      // Fetch related products from the same category
      if (r.data.category) {
        axios
          .get(`${API}/products`, { params: { category: r.data.category } })
          .then(rr => setRelated((rr.data || []).filter(x => x.slug !== r.data.slug).slice(0, 8)))
          .catch(() => setRelated([]));
      } else {
        setRelated([]);
      }
    });
  }, [slug]);

  if (!p) return <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 text-center text-[color:var(--c-grey-500)]">Loading...</div>;

  const variant = p.variants.find(v => v.id === selectedVariantId) || p.variants[0];
  const hasMultipleVariants = p.variants.length > 1;
  const isOnSale = variant?.compare_at_price && variant.compare_at_price > variant.price;
  const unitPrice = variant?.price || 0;
  const lineTotal = unitPrice * qty;

  // Pack-vs-box savings nudge — compares the currently selected variant's per-unit
  // price against the cheapest larger-pack variant (by price/qty heuristic),
  // assuming variant titles like "10 Pack", "100 Pack", "Case of 200" etc.
  const parseUnits = (title = "") => {
    const m = title.match(/(\d+)\s*(?:pack|pcs?|pieces?|case|box|count)/i);
    if (m) return parseInt(m[1], 10);
    const n = title.match(/\b(\d{2,4})\b/);
    return n ? parseInt(n[1], 10) : null;
  };
  const currentUnits = parseUnits(variant?.title) || 1;
  const bestBulk = hasMultipleVariants
    ? p.variants
        .filter(v => v.id !== variant?.id && v.available && (parseUnits(v.title) || 1) > currentUnits)
        .map(v => {
          const u = parseUnits(v.title) || 1;
          return { v, u, perUnit: v.price / u };
        })
        .sort((a, b) => a.perUnit - b.perUnit)[0]
    : null;
  const currentPerUnit = unitPrice / currentUnits;
  const savingsPct = bestBulk && currentPerUnit > 0
    ? Math.round(((currentPerUnit - bestBulk.perUnit) / currentPerUnit) * 100)
    : 0;

  const gallery = (p.gallery && p.gallery.length) ? p.gallery : (p.image ? [p.image] : []);

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.name,
    description: p.description,
    image: p.image,
    sku: p.id,
    category: p.category,
    brand: { "@type": "Brand", name: "Crown01" },
    offers: {
      "@type": "AggregateOffer",
      lowPrice: p.min_price,
      highPrice: p.max_price,
      priceCurrency: p.currency || "GBP",
      offerCount: p.variants.length,
      availability: p.available ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      seller: { "@type": "Organization", name: "Crown01" },
    },
  };

  return (
    <div className="bg-white pb-20 md:pb-12">
      <SEO
        title={p.name}
        description={(p.description || p.name).slice(0, 160)}
        path={`/product/${p.slug}`}
        image={p.image}
        jsonLd={productSchema}
      />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8">
        <div className="text-xs uppercase tracking-[0.18em] mb-5 md:mb-6" style={{ color: "var(--c-gold-deep)" }}>
          <Link to="/" className="hover:underline">Home</Link> · <Link to="/shop" className="hover:underline">Shop</Link> · {p.category}
        </div>
        <div className="grid lg:grid-cols-2 gap-8 md:gap-10">
          <ProductGallery
            gallery={gallery}
            name={p.name}
            active={active}
            setActive={setActive}
            onZoom={() => setLightboxOpen(true)}
          />
          <div>
            <div className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--c-gold-deep)" }}>{p.category}</div>
            <h1 className="font-display text-3xl md:text-4xl text-black mt-2" data-testid="pdp-name">{p.name}</h1>

            <div className="mt-5 flex items-baseline gap-3">
              <div className="font-display text-3xl text-black" data-testid="pdp-price">£{unitPrice.toFixed(2)}</div>
              {isOnSale && <div className="text-lg text-[color:var(--c-grey-500)] line-through">£{variant.compare_at_price.toFixed(2)}</div>}
              {isOnSale && <span className="badge-pill" style={{ background: "var(--c-red)", color: "#fff" }}>Sale</span>}
              {currentUnits > 1 && (
                <div className="text-xs text-[color:var(--c-grey-500)]">≈ £{currentPerUnit.toFixed(2)} per unit</div>
              )}
            </div>

            {p.description && (
              isBundleProduct(p) ? (
                <BundleDetailLayout p={p} />
              ) : (
                <div className="mt-5 text-[color:var(--c-grey-700)] leading-relaxed" data-testid="pdp-description"
                  dangerouslySetInnerHTML={p.description_html ? { __html: p.description_html } : undefined}>
                  {p.description_html ? null : p.description}
                </div>
              )
            )}

            {hasMultipleVariants && (
              <div className="mt-7">
                <div className="text-xs uppercase tracking-[0.18em] mb-3 font-semibold" style={{ color: "var(--c-gold-deep)" }}>Choose your pack size</div>
                <div className="grid grid-cols-2 gap-2.5" data-testid="pdp-variants">
                  {p.variants.map(v => {
                    const u = parseUnits(v.title) || 1;
                    const per = v.price / u;
                    const selected = selectedVariantId === v.id;
                    return (
                      <button
                        key={v.id}
                        onClick={() => setSelectedVariantId(v.id)}
                        disabled={!v.available}
                        className={`p-3 rounded-xl text-left border-2 transition disabled:opacity-40 disabled:cursor-not-allowed ${selected ? "border-[color:var(--c-black)] bg-[color:var(--c-grey-50)]" : "border-[color:var(--c-grey-100)] hover:border-[color:var(--c-grey-200)]"}`}
                        data-testid={`variant-${v.id.split('/').pop()}`}
                      >
                        <div className="text-sm font-semibold text-black leading-tight">{v.title}</div>
                        <div className="text-xs text-[color:var(--c-grey-500)] mt-0.5">£{v.price.toFixed(2)}{!v.available ? " · Sold out" : ""}</div>
                        {u > 1 && (
                          <div className="text-[10px] mt-1 font-semibold" style={{ color: "var(--c-gold-deep)" }}>£{per.toFixed(2)} / unit</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Pack-vs-box savings nudge */}
            {bestBulk && savingsPct >= 8 && (
              <button
                onClick={() => setSelectedVariantId(bestBulk.v.id)}
                className="mt-5 w-full text-left rounded-2xl p-4 flex items-center gap-3 transition hover:brightness-95"
                style={{ background: "var(--c-cream)", border: "1px solid var(--c-gold)" }}
                data-testid="pdp-savings-nudge"
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--c-black)" }}>
                  <i className="fa-solid fa-piggy-bank" style={{ color: "var(--c-gold)" }}></i>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-black">Save {savingsPct}% per unit with the {bestBulk.v.title}</div>
                  <div className="text-xs text-[color:var(--c-grey-700)] mt-0.5">£{bestBulk.perUnit.toFixed(2)}/unit vs £{currentPerUnit.toFixed(2)}/unit — switch and save.</div>
                </div>
                <i className="fa-solid fa-arrow-right" style={{ color: "var(--c-gold-deep)" }}></i>
              </button>
            )}

            <div className="mt-6 flex items-center gap-3 md:gap-4">
              <div className="inline-flex items-center border border-[color:var(--c-grey-200)] rounded-full overflow-hidden" data-testid="pdp-qty">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-12 hover:bg-[color:var(--c-grey-50)]" data-testid="pdp-qty-dec">−</button>
                <span className="px-4 font-semibold min-w-[2rem] text-center" data-testid="pdp-qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="w-10 h-12 hover:bg-[color:var(--c-grey-50)]" data-testid="pdp-qty-inc">+</button>
              </div>
              <button onClick={() => add(p, variant, qty)} disabled={!variant?.available} className="btn btn-red flex-1 disabled:opacity-50" data-testid="pdp-add-to-cart">
                <i className="fa-solid fa-bag-shopping"></i> {variant?.available ? `Add to Cart — £${lineTotal.toFixed(2)}` : "Sold Out"}
              </button>
            </div>

            {/* Trust strip — directly under the buy box */}
            <div className="mt-5 rounded-2xl p-3 md:p-4 grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 text-center md:text-left" style={{ background: "var(--c-grey-50)", border: "1px solid var(--c-grey-100)" }} data-testid="pdp-trust-strip">
              {[
                { i: "fa-warehouse", t: "UK Warehouse", s: "Enfield, EN2 9BS" },
                { i: "fa-truck-fast", t: "Same-day dispatch", s: "Order by 2pm" },
                { i: "fa-shield-halved", t: "Secure checkout", s: "Shopify protected" },
                { i: "fa-rotate-left", t: "14-day returns", s: "Hassle-free" },
              ].map(x => (
                <div key={x.t} className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2.5 p-2 md:p-1.5">
                  <i className={`fa-solid ${x.i} text-base shrink-0`} style={{ color: "var(--c-gold-deep)" }}></i>
                  <div className="min-w-0">
                    <div className="text-[11px] md:text-xs font-semibold text-black leading-tight">{x.t}</div>
                    <div className="text-[10px] text-[color:var(--c-grey-500)] hidden md:block">{x.s}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Related products rail */}
      {related.length > 0 && (
        <section className="border-t" style={{ borderColor: "var(--c-grey-100)" }} data-testid="pdp-related-rail">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-14">
            <div className="flex items-end justify-between gap-3 mb-6">
              <div>
                <div className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--c-gold-deep)" }}>You may also like</div>
                <h2 className="font-display text-2xl md:text-3xl text-black mt-1">More from {p.category}</h2>
              </div>
              <Link to={`/shop?category=${encodeURIComponent(p.category)}`} className="hidden md:inline-flex items-center gap-1 text-sm font-semibold underline" style={{ color: "var(--c-black)" }}>
                See all <i className="fa-solid fa-arrow-right text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {related.slice(0, 4).map(r => <ProductCard key={r.id} p={r} />)}
            </div>
          </div>
        </section>
      )}

      <ProvenMarketPerformance />

      {/* Mobile sticky Add-to-Cart bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t backdrop-blur" style={{ borderColor: "var(--c-grey-100)", background: "rgba(255,255,255,0.97)" }} data-testid="pdp-sticky-cta">
        <div className="max-w-7xl mx-auto px-3 py-2.5 flex items-center gap-2">
          <div className="min-w-0 flex-shrink">
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--c-grey-500)] leading-none">{variant?.title || "Selected option"}</div>
            <div className="flex items-baseline gap-1.5 mt-0.5">
              <div className="font-display text-lg text-black">£{unitPrice.toFixed(2)}</div>
              {currentUnits > 1 && <div className="text-[10px] text-[color:var(--c-grey-500)]">£{currentPerUnit.toFixed(2)}/u</div>}
            </div>
          </div>
          <div className="inline-flex items-center border border-[color:var(--c-grey-200)] rounded-full overflow-hidden h-10 ml-auto">
            <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-8 h-10" data-testid="pdp-sticky-qty-dec">−</button>
            <span className="px-2 font-semibold text-sm min-w-[1.5rem] text-center">{qty}</span>
            <button onClick={() => setQty(qty + 1)} className="w-8 h-10" data-testid="pdp-sticky-qty-inc">+</button>
          </div>
          <button onClick={() => add(p, variant, qty)} disabled={!variant?.available} className="btn btn-red !py-2.5 !px-4 !text-xs whitespace-nowrap disabled:opacity-50" data-testid="pdp-sticky-add-to-cart">
            <i className="fa-solid fa-bag-shopping"></i> {variant?.available ? "Add" : "Sold out"}
          </button>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <ImageLightbox
          images={gallery}
          initialIndex={active}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}

/* -------------------- Product Gallery (mobile swipe + lightbox) -------------------- */
function ProductGallery({ gallery, name, active, setActive, onZoom }) {
  const trackRef = React.useRef(null);
  const total = gallery.length;

  // Sync scroll-snap position with active index on mobile
  const goTo = (i) => {
    setActive(i);
    const el = trackRef.current;
    if (!el) return;
    const w = el.clientWidth;
    el.scrollTo({ left: w * i, behavior: "smooth" });
  };

  // Detect which slide the user has swiped to on mobile
  const onScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    const w = el.clientWidth;
    const idx = Math.round(el.scrollLeft / Math.max(w, 1));
    if (idx !== active) setActive(idx);
  };

  if (!total) {
    return (
      <div className="aspect-square rounded-2xl bg-[color:var(--c-grey-50)] flex items-center justify-center text-[color:var(--c-grey-200)]">
        <i className="fa-solid fa-image text-5xl"></i>
      </div>
    );
  }

  return (
    <div>
      {/* Desktop: single image with zoom; Mobile: horizontal swipe track */}
      <div
        ref={trackRef}
        onScroll={onScroll}
        className="relative aspect-square rounded-2xl overflow-hidden bg-[color:var(--c-grey-50)] flex md:block snap-x snap-mandatory overflow-x-auto md:overflow-hidden no-scrollbar"
        style={{ scrollbarWidth: "none" }}
        data-testid="pdp-gallery"
      >
        {gallery.map((g, i) => (
          <button
            key={i}
            onClick={onZoom}
            className="snap-center shrink-0 w-full h-full md:hidden cursor-zoom-in"
            aria-label={`View image ${i + 1}`}
            data-testid={`pdp-gallery-slide-${i}`}
          >
            <img src={g} alt={`${name} ${i + 1}`} className="w-full h-full object-cover" loading={i === 0 ? "eager" : "lazy"} />
          </button>
        ))}
        {/* Desktop main image */}
        <button onClick={onZoom} className="hidden md:block w-full h-full cursor-zoom-in" aria-label="Zoom image" data-testid="pdp-gallery-zoom">
          <img src={gallery[active]} alt={name} className="w-full h-full object-cover" />
        </button>
        {/* Mobile dots */}
        {total > 1 && (
          <div className="md:hidden absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {gallery.map((_, i) => (
              <span key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: i === active ? "var(--c-black)" : "rgba(0,0,0,0.25)" }} />
            ))}
          </div>
        )}
        {/* Zoom hint */}
        <div className="hidden md:flex absolute top-3 right-3 items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider pointer-events-none" style={{ background: "rgba(10,10,10,0.65)", color: "#fff" }}>
          <i className="fa-solid fa-magnifying-glass-plus"></i> Click to zoom
        </div>
      </div>

      {/* Thumbnails (visible everywhere when >1 image) */}
      {total > 1 && (
        <div className="mt-3 flex gap-2 flex-wrap" data-testid="pdp-gallery-thumbs">
          {gallery.slice(0, 8).map((g, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className="w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden border-2 transition"
              style={{ borderColor: i === active ? "var(--c-gold)" : "var(--c-grey-100)", opacity: i === active ? 1 : 0.7 }}
              data-testid={`pdp-thumb-${i}`}
              aria-label={`Show image ${i + 1}`}
            >
              <img src={g} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------- Image Lightbox -------------------- */
function ImageLightbox({ images, initialIndex, onClose }) {
  const [idx, setIdx] = useState(initialIndex || 0);
  const total = images.length;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx(i => (i + 1) % total);
      if (e.key === "ArrowLeft") setIdx(i => (i - 1 + total) % total);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, total]);

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/92 flex flex-col items-center justify-center p-4 md:p-8"
      onClick={onClose}
      data-testid="pdp-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
        aria-label="Close zoom"
        data-testid="pdp-lightbox-close"
      >
        <i className="fa-solid fa-xmark text-lg"></i>
      </button>
      <img
        src={images[idx]}
        alt=""
        onClick={(e) => e.stopPropagation()}
        className="max-w-full max-h-[78vh] md:max-h-[85vh] object-contain"
      />
      {total > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setIdx((idx - 1 + total) % total); }}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
            aria-label="Previous image"
            data-testid="pdp-lightbox-prev"
          >
            <i className="fa-solid fa-chevron-left"></i>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setIdx((idx + 1) % total); }}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
            aria-label="Next image"
            data-testid="pdp-lightbox-next"
          >
            <i className="fa-solid fa-chevron-right"></i>
          </button>
          <div className="mt-4 text-white/70 text-xs" onClick={(e) => e.stopPropagation()}>
            {idx + 1} / {total}
          </div>
        </>
      )}
    </div>
  );
}

function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const { identifyCart } = useCart();
  const onChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/contact`, form);
      toast.success("Message sent", { description: r.data.message });
      try { trackLead("contact"); } catch (e) { /* noop */ }
      await identifyCart(form.email, { silent: true });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (e) {
      toast.error("Could not send message. Try again.");
    } finally { setLoading(false); }
  };
  return (
    <div className="bg-white">
      <SEO
        title="Contact Crown01 — Live UK Customer Support"
        description="Get in touch with Crown01. Real UK humans, replies within 1 business hour. Bulk quotes, trade accounts and product enquiries welcome."
        path="/contact"
      />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20 grid lg:grid-cols-2 gap-12">
        <div>
          <figure className="mb-8 rounded-2xl overflow-hidden relative" style={{ border: "1px solid var(--c-grey-100)" }} data-testid="contact-team-hero">
            <img
              src="/warehouse/crown01-team-uk-warehouse.jpg"
              alt="The Crown01 team at the UK warehouse, ready to help"
              loading="lazy"
              className="w-full h-auto object-cover block"
              style={{ maxHeight: "260px" }}
            />
            <figcaption
              className="absolute bottom-0 left-0 right-0 px-5 py-3 text-white"
              style={{ background: "linear-gradient(0deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.05) 100%)" }}
            >
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: "var(--c-gold)" }}>UK Team · Enfield</div>
              <div className="font-display text-base md:text-lg mt-0.5">Speak with the Crown01 team.</div>
            </figcaption>
          </figure>
          <div className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--c-gold-deep)" }}>Contact</div>
          <h1 className="font-display text-4xl md:text-5xl text-black mt-2">Talk to a real human.</h1>
          <p className="mt-4 text-[color:var(--c-grey-700)] text-lg max-w-md">Bulk quotes, trade accounts, or product questions — our UK team replies in 1 business hour.</p>
          <div className="mt-8 space-y-4">
            {[
              { i: "fa-envelope", t: "Email", v: "info@crown01.co.uk", href: "mailto:info@crown01.co.uk" },
              { i: "fa-comments", t: "Customer support", v: "Real UK team — reply within 1 business hour" },
              { i: "fa-truck-ramp-box", t: "Trade & bulk", v: "Pallet pricing & Net 30 terms available" },
              { i: "fa-location-dot", t: "Warehouse", v: "95 Theobalds Park Road, Enfield, EN2 9BS, UK" },
            ].map(x => (
              <div key={x.t} className="flex items-start gap-4 p-4 rounded-2xl" style={{ background: "var(--c-grey-50)", border: "1px solid var(--c-grey-100)" }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--c-black)" }}>
                  <i className={`fa-solid ${x.i}`} style={{ color: "var(--c-gold)" }}></i>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] font-semibold" style={{ color: "var(--c-gold-deep)" }}>{x.t}</div>
                  <div className="font-display text-lg text-black mt-0.5">
                    {x.href ? <a href={x.href} className="hover:underline">{x.v}</a> : x.v}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Company trust card */}
          <div className="mt-6 p-5 rounded-2xl" style={{ background: "var(--c-black)", color: "#fff" }} data-testid="contact-company-trust">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-2" style={{ color: "var(--c-gold)" }}>About the Company</div>
            <p className="text-sm text-white/85 leading-relaxed">
              Operated by <strong className="text-white">NAR London Ltd</strong>, a UK-based supplier of packaging, cleaning, catering and wholesale consumable products. We supply retail customers, businesses, wholesalers and pallet buyers across the United Kingdom.
            </p>
            <div className="mt-3 text-xs text-white/65 leading-relaxed">
              <strong className="text-white/85">NAR London Ltd</strong> · trading as <strong className="text-white/85">Crown01</strong><br />
              95 Theobalds Park Road, Enfield, EN2 9BS, United Kingdom · <a href="mailto:info@crown01.co.uk" className="underline" style={{ color: "var(--c-gold)" }}>info@crown01.co.uk</a>
            </div>
          </div>

          <p className="mt-4 text-xs text-[color:var(--c-grey-500)]"><em>Note: our warehouse is not open to walk-in customers. All orders are placed online or via trade enquiry.</em></p>
        </div>
        <form onSubmit={submit} className="p-6 md:p-8 rounded-2xl space-y-4" style={{ background: "var(--c-grey-50)", border: "1px solid var(--c-grey-100)" }} data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--c-grey-500)]">Name *</label>
              <input required name="name" value={form.name} onChange={onChange} className="input-box mt-1" data-testid="contact-name" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--c-grey-500)]">Email *</label>
              <input required type="email" name="email" value={form.email} onChange={onChange} className="input-box mt-1" data-testid="contact-email" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--c-grey-500)]">Phone</label>
              <input name="phone" value={form.phone} onChange={onChange} className="input-box mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--c-grey-500)]">Subject</label>
              <input name="subject" value={form.subject} onChange={onChange} placeholder="Bulk quote, product enquiry..." className="input-box mt-1" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--c-grey-500)]">Message *</label>
            <textarea required name="message" value={form.message} onChange={onChange} rows={6} className="input-box mt-1" data-testid="contact-message"></textarea>
          </div>
          <button disabled={loading} className="btn btn-red w-full" data-testid="contact-submit">
            {loading ? "Sending..." : (<>Send Message <i className="fa-solid fa-arrow-right"></i></>)}
          </button>
          <p className="text-xs text-[color:var(--c-grey-500)] text-center">By submitting, you agree to be contacted by Crown01 about your enquiry.</p>
        </form>
      </div>
    </div>
  );
}

function TradePage() {
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    vat_number: "",
    monthly_usage: "",
    products_of_interest: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { identifyCart } = useCart();
  const onChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/trade-application`, form);
      toast.success("Application received", { description: r.data.message });
      try { trackLead("trade_application"); } catch (e) { /* noop */ }
      await identifyCart(form.email, { silent: true });
      setSent(true);
      setForm({ company_name: "", contact_name: "", email: "", phone: "", vat_number: "", monthly_usage: "", products_of_interest: "" });
    } catch (e) {
      toast.error("Could not submit. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-white">
      <SEO
        title="Trade & Wholesale Pricing — Apply in 60 Seconds"
        description="Apply for a Crown01 trade account. Bespoke wholesale and pallet pricing for restaurants, contractors, retailers, distributors and UK trade buyers. Reply within 1 business hour."
        path="/trade"
      />
      {/* Page hero */}
      <div className="relative overflow-hidden" style={{ background: "var(--c-black)" }}>
        <div className="absolute inset-0 opacity-10 hero-pattern"></div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20 relative">
          <div className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--c-gold)" }}>
            <Link to="/" className="hover:underline text-white/70">Home</Link> · Trade &amp; Wholesale
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-white mt-3 leading-[1.02]">
            <span className="text-gradient-gold">Trade &amp; Wholesale</span> pricing.<br />Apply in 60 seconds.
          </h1>
          <p className="mt-5 text-white/75 text-lg max-w-2xl">
            Bespoke pricing for restaurants, cafés, cleaning contractors, retailers, distributors and trade buyers. No long contracts, no minimum spend — just better rates the more you order.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/70">
            <span><i className="fa-solid fa-check mr-2" style={{ color: "var(--c-gold)" }}></i> Bespoke trade pricing</span>
            <span><i className="fa-solid fa-check mr-2" style={{ color: "var(--c-gold)" }}></i> Pallet quantities welcome</span>
            <span><i className="fa-solid fa-check mr-2" style={{ color: "var(--c-gold)" }}></i> Reply within 1 business hour</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20 grid lg:grid-cols-[1fr_1.1fr] gap-12">
        {/* Left – benefits */}
        <div>
          <SectionTitle eyebrow="Why Crown01 Trade" title="Built for businesses big and small." />
          <p className="mt-4 text-[color:var(--c-grey-700)] leading-relaxed max-w-md">
            Whether you order 5 boxes a month for your café or full pallets for a national contract — we offer the same trusted UK supply chain with pricing that scales with you.
          </p>
          <div className="mt-8 space-y-4">
            {[
              { i: "fa-pound-sign", t: "Tiered trade pricing", d: "Better rates as your volume grows — automatically applied to your account." },
              { i: "fa-truck-ramp-box", t: "Pallet & part-pallet orders", d: "From single cases to full pallets — pick whatever quantity suits you." },
              { i: "fa-headset", t: "Dedicated account manager", d: "A real UK contact who knows your business and your usual order." },
              { i: "fa-clock", t: "Fast quote turnaround", d: "Most quotes returned within 1 business hour during UK working days." },
              { i: "fa-handshake", t: "No long-term contracts", d: "Order as little or as often as you need. Stay flexible." },
            ].map(x => (
              <div key={x.t} className="flex gap-4 p-4 rounded-2xl" style={{ background: "var(--c-grey-50)", border: "1px solid var(--c-grey-100)" }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--c-black)" }}>
                  <i className={`fa-solid ${x.i}`} style={{ color: "var(--c-gold)" }}></i>
                </div>
                <div>
                  <div className="font-display text-lg text-black">{x.t}</div>
                  <div className="text-sm text-[color:var(--c-grey-700)] mt-0.5 leading-relaxed">{x.d}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 p-5 rounded-2xl" style={{ background: "var(--c-cream)", border: "1px solid var(--c-grey-100)" }}>
            <div className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--c-gold-deep)" }}>Just browsing?</div>
            <div className="font-display text-lg text-black mt-1">You don't need a trade account to order.</div>
            <p className="text-sm text-[color:var(--c-grey-700)] mt-1">Anyone can buy single packs or full boxes from <Link to="/shop" className="underline font-semibold">our shop</Link> — no application needed.</p>
          </div>
        </div>

        {/* Right – form */}
        <div>
          {sent ? (
            <div className="p-8 md:p-10 rounded-2xl text-center" style={{ background: "var(--c-grey-50)", border: "1px solid var(--c-grey-100)" }} data-testid="trade-success">
              <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-5" style={{ background: "var(--c-black)" }}>
                <i className="fa-solid fa-check text-2xl" style={{ color: "var(--c-gold)" }}></i>
              </div>
              <h2 className="font-display text-3xl text-black">Application received.</h2>
              <p className="mt-3 text-[color:var(--c-grey-700)] max-w-md mx-auto">A Crown01 trade account manager will email your bespoke pricing within 1 business hour. We've also sent you a confirmation email — please check your inbox.</p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link to="/shop" className="btn btn-black" data-testid="trade-back-shop">Continue Shopping</Link>
                <button onClick={() => setSent(false)} className="btn btn-outline-black">Submit another</button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="p-6 md:p-8 rounded-2xl space-y-4" style={{ background: "var(--c-grey-50)", border: "1px solid var(--c-grey-100)" }} data-testid="trade-form">
              <div className="flex items-center gap-3 pb-4 mb-2" style={{ borderBottom: "1px solid var(--c-grey-100)" }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "var(--c-black)" }}>
                  <i className="fa-solid fa-clipboard-list" style={{ color: "var(--c-gold)" }}></i>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--c-gold-deep)" }}>Trade Account Application</div>
                  <div className="font-display text-xl text-black">A few quick details</div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--c-grey-500)]">Company Name *</label>
                  <input required name="company_name" value={form.company_name} onChange={onChange} className="input-box mt-1" data-testid="trade-company" placeholder="e.g. Crown Café Ltd" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--c-grey-500)]">Contact Name *</label>
                  <input required name="contact_name" value={form.contact_name} onChange={onChange} className="input-box mt-1" data-testid="trade-contact-name" placeholder="Full name" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--c-grey-500)]">Email *</label>
                  <input required type="email" name="email" value={form.email} onChange={onChange} className="input-box mt-1" data-testid="trade-email" placeholder="orders@yourbusiness.co.uk" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--c-grey-500)]">Phone *</label>
                  <input required name="phone" value={form.phone} onChange={onChange} className="input-box mt-1" data-testid="trade-phone" placeholder="+44 ..." />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--c-grey-500)]">VAT Number <span className="opacity-60 normal-case font-normal">(optional)</span></label>
                  <input name="vat_number" value={form.vat_number} onChange={onChange} className="input-box mt-1" data-testid="trade-vat" placeholder="GB123 4567 89" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--c-grey-500)]">Estimated Monthly Usage *</label>
                  <select required name="monthly_usage" value={form.monthly_usage} onChange={onChange} className="input-box mt-1" data-testid="trade-usage">
                    <option value="">Select a range</option>
                    <option>Under £250 / month</option>
                    <option>£250 – £1,000 / month</option>
                    <option>£1,000 – £5,000 / month</option>
                    <option>£5,000 – £15,000 / month</option>
                    <option>£15,000+ / month (pallet quantities)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--c-grey-500)]">Products of Interest *</label>
                <textarea required name="products_of_interest" value={form.products_of_interest} onChange={onChange} rows={4} className="input-box mt-1" data-testid="trade-products" placeholder="e.g. Heavy-duty refuse sacks (90L), catering serviettes, blue centrefeed rolls, vinyl gloves..."></textarea>
              </div>
              <button disabled={loading} className="btn btn-red w-full !py-3.5 !text-base" data-testid="trade-submit">
                {loading ? "Sending..." : (<>Request Trade Pricing <i className="fa-solid fa-arrow-right"></i></>)}
              </button>
              <p className="text-xs text-[color:var(--c-grey-500)] text-center">By submitting, you agree to be contacted by Crown01 about your trade enquiry. We never share your details.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------- Layout / App -------------------- */
function ScrollToTop() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    initAnalytics();
  }, []);
  useEffect(() => {
    window.scrollTo(0, 0);
    // GA4 / Meta / TikTok page_view on every SPA route change
    trackPageView(pathname + search, document.title);
  }, [pathname, search]);
  return null;
}

function Layout({ children }) {
  return (
    <div className="App overflow-x-hidden">
      <Header />
      <CartDrawer />
      <main>{children}</main>
      <Footer />
      <CookieConsent />
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Layout>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/product/:slug" element={<ProductPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/trade" element={<TradePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/returns" element={<ReturnsPage />} />
              <Route path="/shipping" element={<ShippingPage />} />
              <Route path="/cookies" element={<CookiesPage />} />
              <Route path="/wholesale" element={<WholesaleInfoPage />} />
              {/* Careers (public) */}
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/careers/apply/success" element={<ApplicationSuccessPage />} />
              <Route path="/careers/:slug" element={<OpportunityDetailPage />} />
              <Route path="/careers/:slug/apply" element={<OpportunityApplyPage />} />
              <Route path="*" element={<HomePage />} />
            </Routes>
          </Layout>
        </BrowserRouter>
      </CartProvider>
    </HelmetProvider>
  );
}

export default App;
